import {
  AfterViewInit,
  Component,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatButtonModule } from "@angular/material/button";
import { Router, ActivatedRoute } from "@angular/router";
import { Location } from "@angular/common";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { LoaderComponent } from "../../Pages/common/loader/loader.component";
import { FooterComponent } from "../../Pages/common/footer/footer.component";
import { DataUnavailableComponent } from "../../Pages/common/data-unavailable/data-unavailable.component";
import {
  maskEmail,
  maskMobile,
  maskContact,
} from "../../core/utils/mask-utils";
import { HttpService } from "../../Services/http";
import { AuthService, TokenService } from "../../Services/auth";
import { AlertService } from "../../Services/notifications/alert.service";
import { EncryptionService } from "../../Services/encryption";
import { LoggerService } from "../../Services/logger";
import { ValidatorService } from "../../Services/platform";
import { LoaderService } from "../../Services/Common/loader.service";
import { environment } from "../../../environments/environment";

@Component({
  selector: "app-sign-up",
  imports: [
    LoaderComponent,
    FormsModule,
    FooterComponent,
    DataUnavailableComponent,
    ReactiveFormsModule,
    CommonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
  ],
  templateUrl: "./sign-up.component.html",
  styleUrl: "./sign-up.component.scss",
})
export class SignUpComponent implements AfterViewInit, OnInit, OnDestroy {
  inlineOtpLength: number = 6;
  inlineMfaOtpBoxes: string[] = [];
  isSmallScreen: boolean = false;
  signUpData: any = null;
  userInput: string | undefined;
  currentStep: number = 0;
  formValues: { [key: string]: any } = {};
  showError: boolean = false;
  isSubmitting: boolean = false;
  showForgotLink: boolean = true;
  failedAttempts: number = 0;

  activeForm: string = "signupForm";
  userIdNotFound: string | null = null;
  emailFieldErrors: { [key: string]: string } = {};
  activationSent: boolean = false;
  activationMessage: string | null = null;
  accountLocked: boolean = false;
  activationTimerSeconds: number = 0;
  private activationIntervalId: any = null;
  private activationTimerEnd: number | null = null;
  private arrowKeydownListener: ((event: KeyboardEvent) => void) | null = null;
  @Input() control!: FormControl;
  @Input() name: string = "";
  captchaControl: FormControl = new FormControl("");
  isFirstTimeUser: boolean = false;
  forgotPasswordInstructionsSent: boolean = false;
  private verifyingToastId: string | null = null;
  private mfaOtpToastId: string | null = null;
  showloading: boolean = true;
  showContent: boolean = false;
  currentService: string | null = null;
  // MFA states
  mfaMethod: "mobile" | "email" | null = null;
  mfaOtpSent: boolean = false;
  isMfaOtpLoading: boolean = false;
  mfaOtpError: string | null = null;
  captchaError: string | null = null;
  mfaTimerSeconds: number = 0;
  private mfaIntervalId: any = null;
  private mfaTimerEnd: number | null = null;
  mfaPreloaded: boolean = false;
  pendingLoginResponse: any = null;
  private pendingPassword: string | null = null;
  private blockedMessageShown = false;
  private blockedMessageTimeout: any = null;
  mfaEmailDisplay: string = "";
  mfaMobileDisplay: string = "";
  captchaCode: string = "";
  canvasId: string = "captchaCanvas";
  optkey = "";
  userName: any = "";

  private storageKey = "otb_saved_credentials_v1";
  private prefillUserIdFromQuery: string | null = null;

  get subtitleIsString(): boolean {
    try {
      const s = this.signUpData?.forms?.subtitle;
      return typeof s === "string";
    } catch (e) {
      return false;
    }
  }

  constructor(
    private httpService: HttpService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private authService: AuthService,
    private tokenService: TokenService,
    private alertService: AlertService,
    private encryptionService: EncryptionService,
    private logger: LoggerService,
    private loaderService: LoaderService,
  ) {
    if (typeof window !== "undefined") {
      window.scrollTo(0, 0);
      this.isSmallScreen = window.innerWidth < 400;
    }

    this.route.params.subscribe((p: any) => {
      try {
        if (p && p.service) {
          const svc = String(p.service).toUpperCase();
          const allowed = ["DMTT", "VAT", "BL"];
          if (allowed.includes(svc)) {
            this.currentService = svc;
            try {
              sessionStorage.setItem("signup_service", svc);
            } catch (e) {
              /* ignore */
            }

            if (this.signUpData && this.signUpData.forms) {
              try {
                this.applyServiceOverrides();
              } catch (e) {
                /* ignore */
              }
            }
          } else {
            try {
              sessionStorage.removeItem("signup_service");
            } catch (e) {
              /* ignore */
            }
            try {
              this.router.navigate(["/"]);
            } catch (e) {
              /* ignore */
            }
          }
        } else {
          this.currentService = null;
        }
      } catch (e) {
        this.currentService = null;
      }
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.focusFirstVisibleField();
      try {
        if (this.activeFormHasCaptcha()) this.generateCaptcha();
      } catch (e) {
        /* ignore */
      }
    }, 150);
  } 
  ngOnInit(): void {
    // SSO bootstrap: if a token query param is present, skip all SSR work for /signup.
    // SSR can't run window.location/localStorage anyway, and the heavy ngOnInit
    // (route subs, JSON fetches, captcha) makes the server hold the response for
    // many seconds. Letting the browser hydrate fast lets handleSsoIfPresent
    // redirect immediately to /dashboard/bin/{bin}.
    const isBrowser = typeof window !== "undefined";
    if (!isBrowser) {
      try {
        const hasSsoToken = !!this.route?.snapshot?.queryParamMap?.get("token");
        if (hasSsoToken) return;
      } catch (e) {
        /* ignore */
      }
    }

    // if (this.handleSsoIfPresent()) {
    //     // Stop normal sign-up/login init. We already navigated.
    //     return;
    //   }
    if (this.handleSsoIfPresent()) return;

    try {
      const qp = this.route?.snapshot?.queryParamMap;
      const dmttLogout = qp?.get("dmtt_logout") === "1";
      const blLogout = qp?.get("bl_logout") === "1";
      if (dmttLogout || blLogout) {
        try {
          localStorage.removeItem("bin");
        } catch {
          /* ignore */
        }
        try {
          this.tokenService.clearTokens();
        } catch {
          /* ignore */
        }
      }
    } catch {
      /* ignore */
    }

    try {
      let binExist = this.encryptionService.decrypt(
        localStorage.getItem("bin")?.toString() || "",
      );
      // console.log(binExist);

      // localStorage.setItem("testbin", userId);
      // console.log(
      //   "localstorage testbin",
      //
      // );

      if (binExist !== "") {
        console.log("binExist", binExist);
        this.loaderService.show();
        this.authService.checkSessionValidity(binExist).subscribe({
          next: (resp: any) => {
            console.log(resp, "responseFromAPI");
            try {
              const sessionData = resp?.response || {};
              try {
                const svc = (
                  this.currentService ||
                  (typeof sessionStorage !== "undefined"
                    ? sessionStorage.getItem("signup_service")
                    : null) ||
                  ""
                )
                  .toString()
                  .toUpperCase();
                if (svc === "DMTT") {
                  const userTaxTypeRaw =
                    resp?.response?.user?.taxType ||
                    "";
                  const userTaxType = (userTaxTypeRaw || "").toString().trim().toLowerCase();
                  const restricted = new Set([
                    "individual",
                    "individual resident",
                    "individual non-resident",
                    "entity non-resident",
                  ]);
                  if (userTaxType && restricted.has(userTaxType)) {
                    this.loaderService.hide();
                    this.alertService.show(
                      "You are not authorized to login here.",
                      { type: "error", duration: 4000 },
                    );
                    return;
                  }
                }
              } catch (e) {
                /* ignore tax-type check failures */
              }
              if (sessionData.is_session_active === "Y") {
                // Extract session credentials
                const sessionID = sessionData.token;
                const bin = sessionData.user.binNumber || binExist;

                // Set tokens
                this.tokenService.setAccessToken(sessionID);
                this.tokenService.setUserId(bin);

                // Finalize login like in verifyMfaOtp
                try {
                  this.authService.finalizeLogin(resp, { remember: false });
                } catch (e) {
                  console.warn(
                    "Failed to finalize login from session check",
                    e,
                  );
                }

                this.mfaOtpError = null;
                this.mfaOtpSent = false;
                this.pendingPassword = null;
                this.pendingLoginResponse = null;

                this.loaderService.hide();
                // Handle service-specific routing
                try {
                  const svc = (
                    this.currentService ||
                    (typeof sessionStorage !== "undefined"
                      ? sessionStorage.getItem("signup_service")
                      : null) ||
                    ""
                  )
                    .toString()
                    .toUpperCase();
                  console.log("Current service:", svc);
                  if (svc === "DMTT") {
                    // Get user profile for DMTT redirect
                    const userProfile: any =
                      this.tokenService?.getUserProfile?.() || {};
                    const profileFromSession = {
                      binNumber: bin || "",
                      email: userProfile?.email || null,
                      persona: userProfile?.role || userProfile?.profile || [],
                      tinNumber: userProfile?.tin || null,
                      taxType: userProfile?.taxType || null,
                      firstName: userProfile?.firstName || null,
                      lastName: userProfile?.lastName || null,
                      mobileNumber: userProfile?.phone || null,
                      applicationId: bin || "",
                    };

                    const payload = {
                      token: sessionID,
                      profile: profileFromSession,
                    };

                    const encryptedPayload =
                      this.encryptionService.encrypt(payload);
                    const target = environment.dmttDashboardUrl;
                    window.location.href = `${target}?dmtt_token=${encodeURIComponent(encryptedPayload)}`;
                  } else if (svc === "VAT") {
                    const userProfile: any =
                      this.tokenService?.getUserProfile?.() || {};
                    const profileFromSession = {
                      binNumber: bin || "",
                      email: userProfile?.email || null,
                      persona: userProfile?.role || userProfile?.profile || [],
                      tinNumber: userProfile?.tin || null,
                      taxType: userProfile?.taxType || resp?.response?.user?.taxType || null,
                      firstName: userProfile?.firstName || resp?.response?.user?.firstName || null,
                      lastName: userProfile?.lastName || resp?.response?.user?.lastName || null,
                      middleName: userProfile?.middleName || resp?.response?.user?.middleName || null,
                      mobileNumber: userProfile?.phone || resp?.response?.user?.mobileNumber || null,
                      applicationId: bin || "",
                      registeredName: resp?.response?.user?.registeredName || null,
                      isFirstTimeLogin: resp?.response?.user?.isFirstTimeLogin || null,
                    };

                    const payload = {
                      token: sessionID,
                      profile: profileFromSession,
                    };

                    const encryptedPayload =
                      this.encryptionService.encrypt(payload);
                    const target = environment.vatDashboardUrl;
                    window.location.href = `${target}?token=${encodeURIComponent(encryptedPayload)}`;

                  } else if (svc === "BL") {
                    const userProfile: any =
                      this.tokenService?.getUserProfile?.() || {};
                    const profileFromSession = {
                      binNumber: bin || "",
                      email: userProfile?.email || null,
                      persona: userProfile?.role || userProfile?.profile || [],
                      tinNumber: userProfile?.tin || null,
                      taxType: userProfile?.taxType || resp?.response?.user?.taxType || null,
                      firstName: userProfile?.firstName || resp?.response?.user?.firstName || null,
                      lastName: userProfile?.lastName || resp?.response?.user?.lastName || null,
                      middleName: userProfile?.middleName || resp?.response?.user?.middleName || null,
                      mobileNumber: userProfile?.phone || resp?.response?.user?.mobileNumber || null,
                      applicationId: bin || "",
                      registeredName: resp?.response?.user?.registeredName || null,
                      isFirstTimeLogin: resp?.response?.user?.isFirstTimeLogin || null,
                    };

                    const payload = {
                      token: sessionID,
                      profile: profileFromSession,
                    };

                    const encryptedPayload =
                      this.encryptionService.encrypt(payload);
                    const target = environment.blDashboardUrl;
                    window.location.href = `${target}?token=${encodeURIComponent(encryptedPayload)}`;
                  }
                  else {
                    console.log("Error during service-specific routing:");
                    this.authService.markMfaVerified();
                    this.router.navigate(["/dashboard"]);
                  }
                } catch (e) {
                  console.log("Error during service-specific routing:", e);
                  this.authService.markMfaVerified();
                  this.router.navigate(["/dashboard"]);
                }
              } else {
                console.log("good to go");
                this.authService.signOut();
                this.loaderService.hide();
              }
            } catch (e) {
              this.logger.error(
                "Error checking session validity",
                e,
                "sign-up.component.ts",
              );
              this.loaderService.hide();
              this.alertService.show(
                "Session check failed. Please login again.",
                { type: "error", duration: 4000 },
              );
            }
          },
          error: (err: any) => {
            console.log(err);
            this.loaderService.hide();
          },
        });
      } else {
        this.loaderService.hide();
        this.route.params.subscribe((p: any) => {
          try {
            if (p && p.service) {
              const svc = String(p.service).toUpperCase();
              const allowed = ["DMTT", "VAT", "BL"];
              if (allowed.includes(svc)) {
                this.currentService = svc;
                try {
                  sessionStorage.setItem("signup_service", svc);
                } catch (e) {
                  /* ignore */
                }

                if (this.signUpData && this.signUpData.forms) {
                  try {
                    this.applyServiceOverrides();
                  } catch (e) {
                    /* ignore */
                  }
                }
              } else {
                try {
                  sessionStorage.removeItem("signup_service");
                } catch (e) {
                  /* ignore */
                }
                try {
                  this.router.navigate(["/"]);
                } catch (e) {
                  /* ignore */
                }
              }
            } else {
              this.currentService = null;
            }
          } catch (e) {
            this.currentService = null;
          }
        });
        this.route.queryParams.subscribe((params: any) => {
          const token = (params?.token || "").toString().trim();
          const applicationid = (params?.applicationid || "").toString().trim();
          const bin = (params?.bin || params?.binNumber || "").toString().trim();

          //  If token is a JWT, it will have 2 dots
          const isJwt = token.split(".").length === 3;

          if (token && isJwt) {
            // decode JWT payload (middle part)
            let payload: any = {};
            try {
              const base64 = token
                .split(".")[1]
                .replace(/-/g, "+")
                .replace(/_/g, "/");
              const json = decodeURIComponent(
                atob(base64)
                  .split("")
                  .map(
                    (c) =>
                      "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2),
                  )
                  .join(""),
              );
              payload = JSON.parse(json);
            } catch (e) {
              console.warn("[SSO] Failed to decode JWT payload", e);
            }

            const userId = String(
              payload?.userId || applicationid || bin || "",
            ).trim();
            if (!userId) return;

            // build a login-like response and finalize it
            const ssoResponse = {
              response: {
                token: token,
                expiresAt: payload?.exp
                  ? Number(payload.exp) * 1000
                  : undefined,
                user: {
                  userId: userId,
                  username: payload?.userName || "User",
                  email: payload?.email || null,
                  role: payload?.role || null,
                  profile: payload?.profile || null,
                  binNumber: bin || userId,
                  applicationId: applicationid || userId,
                },
              },
            };

            try {
              this.authService.finalizeLogin(ssoResponse, { remember: false });
            } catch (e) {
              console.warn("[SSO] finalizeLogin failed", e);
            }

            //  MFA/session flag like normal flow
            this.authService.markMfaVerified();

            // go to dashboard
            setTimeout(() => {
              this.router.navigateByUrl("/dashboard", { replaceUrl: true });
            }, 0);

            return;
          }

          // // ✅ SSO entry from App Switcher
          // const token = (params?.token || '').toString().trim();
          // const applicationid = (params?.applicationid || '').toString().trim();
          // const bin = (params?.bin || params?.binNumber || '').toString().trim();
          // const returnUrl = (params?.returnUrl || '/dashboard').toString();

          // if (token) {
          //   // 1) store token
          //   this.tokenService.setAccessToken(token, false);

          //   // 2) TokenService.getUserId() depends on userData.id
          //   const resolvedUserId = applicationid || bin;
          //   const userObj: any = {
          //     id: resolvedUserId,
          //     userId: resolvedUserId,
          //     username: resolvedUserId,
          //     BIN: bin || null,
          //     binNumber: bin || null
          //   };
          //   this.tokenService.setUserData(userObj, false);
          //   this.tokenService.setUserId(resolvedUserId, false);

          //   // 3) mark session as logged in + MFA ok
          //   this.authService.signIn(resolvedUserId);
          //   this.authService.markMfaVerified();
          //   this.authService.refreshAuthState();

          //   // 4) navigate to dashboard (or returnUrl)
          //   const safeReturn = returnUrl.startsWith('/') ? returnUrl : '/' + returnUrl;
          //   this.router.navigateByUrl(safeReturn, { replaceUrl: true });
          //   return;
          // }

          // // ✅ keep your existing bin prefill logic
          // const binNumber = params?.binNumber || null;
          // if (binNumber) {
          //   this.prefillUserIdFromQuery = String(binNumber);
          //   this.formValues['userId'] = this.prefillUserIdFromQuery;
          //   this.setActiveForm('signupForm');
          //   setTimeout(() => this.focusFirstVisibleField(), 150);
          // }

          // const bin = params?.binNumber || null;
          if (bin) {
            this.prefillUserIdFromQuery = String(bin);
            this.formValues["userId"] = this.prefillUserIdFromQuery;
            this.setActiveForm("signupForm");
            setTimeout(() => this.focusFirstVisibleField(), 150);
          }
        });
      }
    } catch (e) {
      /* ignore */
    }
    this.getData();
    this.inlineMfaOtpBoxes = new Array(this.inlineOtpLength).fill("");

    if (typeof document !== "undefined") {
      this.arrowKeydownListener = (event: KeyboardEvent) => {
        try {
          if (!event) return;
          const key = event.key || event.code || "";
          if (!key) return;
          const normalized = key.toLowerCase();
          const isArrow =
            normalized === "arrowleft" ||
            normalized === "arrowright" ||
            normalized === "arrowup" ||
            normalized === "arrowdown" ||
            normalized === "left" ||
            normalized === "right" ||
            normalized === "up" ||
            normalized === "down";
          if (!isArrow) return;

          const target = event.target as HTMLElement | null;
          if (target) {
            const tag = (target.tagName || "").toLowerCase();
            const isInput =
              tag === "input" ||
              tag === "textarea" ||
              tag === "select" ||
              target.isContentEditable;

            if (isInput) {
              // If it's an OTP input, we must allow the event to reach the component's keydown handler
              if (target.closest && target.closest(".otp-input-box")) {
                return;
              }
              // For other inputs, stop propagation to prevent global listeners (like scroll) from firing
              // But do NOT preventDefault, so the cursor can still move
              event.stopPropagation();
              return;
            }
          }

          // If not an input, prevent default scroll
          event.preventDefault();
          event.stopPropagation();
        } catch (err) {
          console.log("WARN: arrowKeydownListener error", err);
        }
      };

      try {
        document.addEventListener("keydown", this.arrowKeydownListener, true);
      } catch (err) {
        console.log("WARN: Unable to attach arrow key listener", err);
      }
      try {
        document.body.classList.add("signup-page-bg");
      } catch (e) {
        // ignore
      }
    }
  }
  // private parseJwtExpiryMs(token: string): number | null {
  //   try {
  //     const parts = (token || '').split('.');
  //     if (parts.length < 2) return null;
  //     const payloadJson = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
  //     const payload = JSON.parse(payloadJson);
  //     const exp = Number(payload?.exp);
  //     if (!Number.isFinite(exp)) return null;
  //     // JWT exp is seconds; convert to ms
  //     return exp > 1e12 ? exp : exp * 1000;
  //   } catch {
  //     return null;
  //   }
  // }

  // private handleSsoIfPresent(): boolean {
  //   try {
  //     const qp = this.route.snapshot.queryParamMap;

  //     const token = (qp.get('token') || '').trim();
  //     if (!token) return false;

  //     const applicationid = (qp.get('applicationid') || '').trim();
  //     const bin = (qp.get('bin') || '').trim();

  //     // choose a userId that your AuthService/TokenService will accept
  //     const resolvedUserId = applicationid || bin;
  //     if (!resolvedUserId) {
  //       // token present but no identifier; fallback to login page
  //       this.router.navigateByUrl('/', { replaceUrl: true });
  //       return true;
  //     }

  //     // 1) store token in TokenService (it will encrypt internally)
  //     this.tokenService.setAccessToken(token, false);

  //     // 2) store minimal user data so tokenService.getUserId() works
  //     // NOTE: your TokenService.getUserId() returns userData.id
  //     const userObj: any = {
  //       id: resolvedUserId,
  //       userId: resolvedUserId,
  //       username: resolvedUserId,
  //       BIN: bin || null,
  //       binNumber: bin || null
  //     };
  //     this.tokenService.setUserData(userObj, false);
  //     this.tokenService.setUserId(resolvedUserId, false);

  //     // 3) set expiry if available from JWT exp
  //     const expiryMs = this.parseJwtExpiryMs(token);
  //     if (expiryMs) {
  //       this.tokenService.setTokenExpiry(expiryMs, false);
  //     }

  //     // 4) start session state
  //     this.authService.signIn(resolvedUserId);
  //     this.authService.markMfaVerified();
  //     this.authService.refreshAuthState();

  //     // 5) redirect to dashboard (or respect returnUrl if present)
  //     const returnUrlRaw = (qp.get('returnUrl') || '').trim();
  //     const returnUrl = returnUrlRaw ? decodeURIComponent(returnUrlRaw) : '/dashboard';

  //     // Ensure it is a relative route
  //     const safeReturn = returnUrl.startsWith('/') ? returnUrl : `/${returnUrl}`;
  //     this.router.navigateByUrl(safeReturn, { replaceUrl: true });

  //     return true;
  //   } catch (e) {
  //     // If anything fails, fallback to normal flow
  //     return false;
  //   }
  // }
  private handleSsoIfPresent(): boolean {
    try {
      const qp = this.route.snapshot.queryParamMap;

      let token = (qp.get("token") || "").trim();
      const appIdParam = (qp.get("applicationid") || "").trim();
      const bin = (qp.get("bin") || "").trim();

      if (!token) return false;

      // If token comes encrypted as iv:cipher, decrypt it
      if (token.includes(":")) {
        const dec = this.encryptionService.decrypt(token);
        if (typeof dec === "string" && dec.trim()) token = dec.trim();
      }

      // If still looks encrypted, stop (secret mismatch)
      if (token.includes(":")) {
        try {
          this.alertService.show(
            "SSO token could not be processed. Please login again.",
            { type: "error", duration: 5000 },
          );
        } catch { }
        return true; // handled, but cannot proceed
      }

      // Decode JWT payload to hydrate user/profile/role
      const decodeJwtPayload = (jwt: string): any => {
        try {
          const parts = jwt.split(".");
          if (parts.length < 2) return null;
          const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
          const json = decodeURIComponent(
            atob(base64)
              .split("")
              .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
              .join(""),
          );
          return JSON.parse(json);
        } catch {
          return null;
        }
      };

      const payload = decodeJwtPayload(token) || {};
      const resolvedUserId = String(
        payload.userId ||
        payload.userid ||
        payload.id ||
        appIdParam ||
        bin ||
        "",
      ).trim();

      if (!resolvedUserId) return false;

      // Token expiry from JWT exp (seconds)
      const expSeconds = Number(payload.exp);
      if (Number.isFinite(expSeconds) && expSeconds > 0) {
        this.tokenService.setTokenExpiry(expSeconds * 1000, false);
      }

      // Store access token
      this.tokenService.setAccessToken(token, false);

      // Store user data INCLUDING role so header can pick it up
      const userObj: any = {
        id: resolvedUserId,
        userId: resolvedUserId,
        username: payload.userName || payload.username || "User",
        email: payload.email || null,
        role: payload.role || null,
        profile: payload.profile || null,
        BIN: bin || resolvedUserId,
        binNumber: bin || resolvedUserId,
      };

      this.tokenService.setUserData(userObj, false);
      this.tokenService.setUserId(resolvedUserId, false);

      // Mark logged in
      this.authService.signIn(resolvedUserId);
      this.authService.markMfaVerified();
      this.authService.refreshAuthState();

      // Redirect BIN-based SSO users straight to their BIN details page.
      // IMPORTANT: navigate via Angular Router (client-side) instead of
      // window.location.replace. A full reload causes SSR to re-render the
      // protected route on the server, where sessionStorage doesn't exist —
      // AuthGuard then bounces the user back to '/' (the sign-in page).
      const targetRoute = bin
        ? `/dashboard/bin/${encodeURIComponent(bin)}`
        : "/dashboard";
      try {
        this.router.navigateByUrl(targetRoute, { replaceUrl: true });
      } catch (navErr) {
        console.warn(
          "[SSO] Router navigation failed, falling back to location.replace",
          navErr,
        );
        try {
          window.location.replace(targetRoute);
        } catch { }
      }

      return true;
    } catch (e) {
      console.warn("handleSsoIfPresent failed:", e);
      return false;
    }
  }

  async getData(): Promise<void> {
    try {
      const data = await this.httpService.getJsonData(
        "assets/JSONData/sign-up.json",
        "",
      );
      if (data?.status === 200) {
        const resp = data.response || {};
        this.signUpData = resp.signUpContent;
        // Ensure registration URL comes from the environment configuration
        try {
          const regUrl =
            environment && environment.traDashboardUrl
              ? environment.traDashboardUrl
              : this.signUpData?.forms?.subtitle?.link?.href || "";
          if (
            this.signUpData &&
            this.signUpData.forms &&
            this.signUpData.forms.subtitle &&
            this.signUpData.forms.subtitle.link
          ) {
            this.signUpData.forms.subtitle.link.href = regUrl;
          }
        } catch (e) {
          /* ignore */
        }
        this.setActiveForm(this.activeForm);
        this.loadSavedCredentials();
        try {
          this.applyServiceOverrides();
        } catch (e) {
          /* ignore */
        }
        this.showContent = true;
        this.showloading = false;
        this.focusFirstVisibleField();
        try {
          if (this.activeFormHasCaptcha()) this.generateCaptcha();
        } catch (e) {
          /* ignore */
        }
      } else if (data?.status === 204) {
        this.showloading = true;
      } else {
        this.showContent = false;
        this.showloading = false;
      }
    } catch (error) {
      this.logger.error("Error fetching data", error, "sign-up.component.ts");
      this.showContent = false;
      this.showloading = false;
    }
  }

  private applyServiceOverrides(): void {
    try {
      if (!this.currentService || !this.signUpData || !this.signUpData.forms)
        return;
      const svc = String(this.currentService).toUpperCase();
      if (
        this.signUpData.forms.title &&
        typeof this.signUpData.forms.title === "string"
      ) {
        this.signUpData.forms.title = this.signUpData.forms.title.replace(
          /OTB/gi,
          svc,
        );
      }
      // if (this.signUpData.forms.subtitle) {
      //   try {
      //     delete this.signUpData.forms.subtitle;
      //   } catch (e) {
      //     this.signUpData.forms.subtitle = null;
      //   }
      // }
    } catch (e) {
      console.log("WARN: applyServiceOverrides failed", e);
    }
  }

  activeFormHasCaptcha(): boolean {
    try {
      const form = this.signUpData?.forms?.[this.activeForm];
      if (!form) return false;
      // explicit captcha field
      const fields = form.fields || [];
      if (
        fields.some(
          (f: any) => f && (f.type === "captcha" || f.key === "captcha"),
        )
      )
        return true;
      // opt-in flag
      if (form.enableCaptcha === true) return true;
      return false;
    } catch (e) {
      return false;
    }
  }

  public getActiveFormField(key: string): any {
    try {
      const form = this.signUpData?.forms?.[this.activeForm];
      if (!form || !form.fields) return null;
      return form.fields.find((f: any) => f && f.key === key) || null;
    } catch (e) {
      return null;
    }
  }

  getFieldLabelFromJson(key: string, fallback: string = ""): string {
    const f = this.getActiveFormField(key);
    if (f && f.label) return f.label;
    return fallback;
  }

  getFieldPlaceholderFromJson(key: string, fallback: string = ""): string {
    const f = this.getActiveFormField(key);

    if (f && f.placeholder) return f.placeholder;
    return fallback;
  }

  onMfaInlineOtpInput(evt: any, idx: number) {
    const ch = (evt.target.value || "").toString().slice(-1);
    if (!/^[0-9]$/.test(ch)) {
      evt.target.value = "";
      return;
    }
    this.mfaOtpError = null;
    this.inlineMfaOtpBoxes[idx] = ch;
    this.formValues["mfaOtp"] = this.inlineMfaOtpBoxes.join("");
    const next = document.querySelectorAll(".otp-input-box input")[idx + 1] as
      | HTMLInputElement
      | undefined;
    const box = document.querySelectorAll(".otp-input-box")[idx] as
      | HTMLElement
      | undefined;
    if (box) box.classList.add("filled");
    if (next) next.focus();
  }

  onMfaInlineOtpKeydown(evt: KeyboardEvent, idx: number) {
    const target = evt.target as HTMLInputElement;
    if (evt.key === "Backspace") {
      if (!target.value) {
        const prev = document.querySelectorAll(".otp-input-box input")[
          idx - 1
        ] as HTMLInputElement | undefined;
        if (prev) {
          prev.focus();
          evt.preventDefault();
        }
      }
      this.inlineMfaOtpBoxes[idx] = "";
      this.formValues["mfaOtp"] = this.inlineMfaOtpBoxes.join("");
      this.mfaOtpError = null;
      const box = document.querySelectorAll(".otp-input-box")[idx] as
        | HTMLElement
        | undefined;
      if (box) box.classList.remove("filled");
      return;
    }
    if (evt.key === "ArrowLeft") {
      const prev = document.querySelectorAll(".otp-input-box input")[
        idx - 1
      ] as HTMLInputElement | undefined;
      if (prev) {
        prev.focus();
        evt.preventDefault();
      }
    }
    if (evt.key === "ArrowRight") {
      const next = document.querySelectorAll(".otp-input-box input")[
        idx + 1
      ] as HTMLInputElement | undefined;
      if (next) {
        next.focus();
        evt.preventDefault();
      }
    }
  }

  onMfaOtpPaste(evt: ClipboardEvent) {
    try {
      evt.preventDefault();
      const text =
        (evt.clipboardData && evt.clipboardData.getData("text")) || "";
      const digits = text
        .replace(/\D/g, "")
        .slice(0, this.inlineOtpLength)
        .split("");
      for (let i = 0; i < this.inlineOtpLength; i++) {
        this.inlineMfaOtpBoxes[i] = digits[i] || "";
        const input = document.querySelectorAll(".otp-input-box input")[i] as
          | HTMLInputElement
          | undefined;
        if (input) {
          input.value = this.inlineMfaOtpBoxes[i] || "";
          const box = input.closest(".otp-input-box") as HTMLElement | null;
          if (box) {
            if (this.inlineMfaOtpBoxes[i]) box.classList.add("filled");
            else box.classList.remove("filled");
          }
        }
      }

      this.formValues["mfaOtp"] = this.inlineMfaOtpBoxes.join("");
      try {
        const inputs = document.querySelectorAll(
          ".otp-input-box input",
        ) as NodeListOf<HTMLInputElement>;
        for (let i = 0; i < inputs.length; i++) {
          const inp = inputs[i];
          if (inp) {
            try {
              inp.dispatchEvent(new Event("input", { bubbles: true }));
            } catch (e) {
              /* ignore */
            }
          }
        }
      } catch (e) {
        /* ignore */
      }

      try {
        const firstEmpty = this.inlineMfaOtpBoxes.findIndex((v) => !v);
        const focusIdx =
          firstEmpty === -1 ? this.inlineOtpLength - 1 : firstEmpty;
        const focusEl = document.querySelectorAll(".otp-input-box input")[
          focusIdx
        ] as HTMLInputElement | undefined;
        if (focusEl) focusEl.focus();
      } catch (e) {
        /* ignore */
      }
    } catch (e) {
      // ignore paste errors
    }
  }

  goToUpdatePassword() {
    let tin = "";
    if (this.formValues["registeredEmail"])
      tin = this.formValues["registeredEmail"] || "";
    else tin = this.formValues["userId"] || "";
    console.log("INFO: goToUpdatePassword tin", tin);
    this.router.navigate(["/update-password"], { queryParams: { tin } });
  }

  startActivationTimer(seconds: number) {
    if (this.activationIntervalId) {
      clearInterval(this.activationIntervalId);
      this.activationIntervalId = null;
    }
    const durationMs = Math.max(0, Number(seconds) || 0) * 1000;
    this.activationTimerEnd = Date.now() + durationMs;
    this.activationTimerSeconds = Math.ceil(durationMs / 1000);
    this.updateActivationCountdownAccurate();
    this.activationIntervalId = setInterval(() => {
      this.updateActivationCountdownAccurate();
    }, 500);
  }

  private updateActivationCountdownAccurate() {
    if (!this.activationTimerEnd) {
      this.activationTimerSeconds = 0;
      return;
    }
    const remaining = this.activationTimerEnd - Date.now();
    if (remaining <= 0) {
      this.activationTimerSeconds = 0;
      if (this.activationIntervalId) {
        clearInterval(this.activationIntervalId);
        this.activationIntervalId = null;
      }
      this.activationTimerEnd = null;
      return;
    }
    this.activationTimerSeconds = Math.ceil(remaining / 1000);
  }

  stopActivationTimer() {
    if (this.activationIntervalId) {
      clearInterval(this.activationIntervalId);
      this.activationIntervalId = null;
    }
    this.activationTimerEnd = null;
    this.activationTimerSeconds = 0;
  }

  resendActivation() {
    if (this.activationTimerSeconds > 0) return;
    const identifier =
      this.formValues["registeredEmail"] ||
      this.formValues["userId"] ||
      this.formValues["tinNumber"] ||
      null;
    delete this.emailFieldErrors["registeredEmail"];
    this.activationSent = true;
    this.startActivationTimer(60);
    if (!identifier) {
      this.alertService.show(
        "No identifier available to resend activation to.",
        { type: "error", duration: 4000 },
      );
      return;
    }

    // Decide linkType based on flow: account locked takes highest priority, then forgot-password, otherwise first-time
    let linkTypeToSend = "First Time Password";
    if (this.accountLocked) {
      linkTypeToSend = "Account Locked - Reset your password";
    } else if (
      this.forgotPasswordInstructionsSent ||
      this.activeForm !== "signupForm"
    ) {
      linkTypeToSend = "Reset Password";
    }
    this.authService.postUpdatePassword(this.formValues["userId"]).subscribe({
      next: (res: any) => {
        const data = res?.response || {};
        console.log(
          "INFO: postUpdatePassword response",
          data,
          "sign-up.component.ts",
        );
        if (data?.flag) {
          try {
            let resetKey = data?.resetKey || "";
            console.log(
              "INFO: Received resetKey from postUpdatePassword",
              resetKey,
              "sign-up.component.ts",
            );
            this.authService
              .resendMail(
                identifier,
                linkTypeToSend,
                this.userName,
                this.formValues["userId"],
                resetKey,
              )
              .subscribe({
                next: (resp: any) => {
                  const data = resp?.response || {};
                  const sent = data?.sent === true || data?.sent === "true";
                  if (sent) {
                    const toValue = data?.to || identifier;
                    const isEmail =
                      typeof toValue === "string" && toValue.includes("@");
                    const display = isEmail
                      ? maskEmail(toValue)
                      : String(toValue);
                    this.alertService.show("Instructions sent to " + display, {
                      type: "success",
                      duration: 3000,
                    });
                  } else {
                    this.alertService.show(
                      data?.message || "Failed to resend instructions",
                      { type: "error", duration: 4000 },
                    );
                  }
                },
                error: (err: any) => {
                  this.logger.error(
                    "resendActivation resendMail failed",
                    err,
                    "sign-up.component.ts",
                  );
                  this.alertService.show(
                    err?.error?.message || "Unable to resend instructions",
                    { type: "error", duration: 4000 },
                  );
                },
              });
          } catch (e) {
            this.logger.warn(
              "Error handling postUpdatePassword success",
              e,
              "sign-up.component.ts",
            );
          }
        } else {
          // Backend indicated not to proceed
          const msg =
            data?.message || "Unable to process password reset request";
          this.alertService.show(msg, { type: "error", duration: 4000 });
          this.focusField("userId");
          return;
        }
      },
      error: (err: any) => {
        console.log(
          "INFO: postUpdatePassword error",
          err,
          "sign-up.component.ts",
        );
        this.logger.error(
          "postUpdatePassword failed",
          err,
          "sign-up.component.ts",
        );
        this.alertService.show(
          err?.error?.message || "Unable to process password reset request",
          { type: "error", duration: 4000 },
        );
        this.focusField("userId");
        return;
      },
    });
  }

  private loadSavedCredentials() {
    if (typeof window === "undefined" || typeof localStorage === "undefined") {
      return;
    }
    try {
      const data = localStorage.getItem(this.storageKey);
      if (!data) return;
      let decoded: any = null;
      try {
        decoded = this.encryptionService.decrypt(data);
      } catch (decryptErr) {
        try {
          decoded = JSON.parse(data);
        } catch (parseErr) {
          decoded = null;
        }
      }
      const savedUserId = decoded?.userId || decoded?.user || null;
      if (!this.prefillUserIdFromQuery && savedUserId) {
        this.formValues["userId"] = savedUserId;
        this.formValues["rememberPassword"] = true;
        if (decoded && decoded.password) {
          this.persistSavedLoginPreference(true, savedUserId);
        }
      }
    } catch (e) {
      console.log("WARN: Unable to load saved credentials", e);
    }
  }

  private persistSavedLoginPreference(remember: boolean, userId?: string) {
    if (typeof window === "undefined" || typeof localStorage === "undefined") {
      return;
    }
    try {
      if (remember && userId) {
        const payload = { userId };
        const enc = this.encryptionService.encrypt(payload);
        localStorage.setItem(this.storageKey, enc);
      } else {
        localStorage.removeItem(this.storageKey);
      }
      this.formValues["rememberPassword"] = !!remember;
    } catch (e) {
      console.log("WARN: Unable to persist login preference", e);
    }
  }

  showField(field: any, index: number): boolean {
    return index <= this.currentStep;
  }

  isFinalField(index: number): boolean {
    const fields = this.signUpData?.forms?.[this.activeForm]?.fields || [];
    let lastIndex = -1;
    for (let i = 0; i < fields.length; i++) {
      if (fields[i].type !== "checkbox") lastIndex = i;
    }
    return index === lastIndex;
  }

  setActiveForm(formKey: string) {
    this.activeForm = formKey;
    this.showError = false;
    this.forgotPasswordInstructionsSent = false;
    this.currentStep = 0;
    // reset first-time user flag when switching forms
    this.isFirstTimeUser = false;
    this.accountLocked = false;

    if (formKey === "mfaLoginForm") {
      if (!this.formValues["mfaMethod"]) this.formValues["mfaMethod"] = "email";
      this.mfaOtpSent = false;
      this.mfaOtpError = null;
      this.mfaTimerSeconds = 0;
      if (this.mfaIntervalId) {
        clearInterval(this.mfaIntervalId);
        this.mfaIntervalId = null;
      }

      const method = this.formValues["mfaMethod"];
      const focusKey = method === "mobile" ? "mfaMobile" : "mfaEmail";
      this.focusField(focusKey, 150);
    }

    if (formKey === "signupForm") {
      this.focusFirstVisibleField();
    }
  }

  onFieldChange(fieldKey: string) {
    this.mfaOtpError = null;
    if (this.emailFieldErrors && this.emailFieldErrors[fieldKey]) {
      delete this.emailFieldErrors[fieldKey];
    }

    // Clear BIN-not-found message when user edits the related fields
    try {
      const fk = (fieldKey || "").toString().toLowerCase();
      if (
        fk.includes("email") ||
        fk.includes("userid") ||
        fk === "userId".toLowerCase()
      ) {
        this.userIdNotFound = null;
      }
    } catch (e) {
      /* ignore */
    }

    if (
      fieldKey === "newPassword" ||
      fieldKey === "confirmPassword" ||
      fieldKey === "password"
    ) {
      const val = this.formValues[fieldKey];
      if (val && typeof val === "string" && val.trim() !== "") {
        this.showError = false;
      }
    }
  }

  onMfaFieldChange(fieldKey: string) {
    this.mfaOtpError = null;
    if (fieldKey === "mfaMobile" || fieldKey === "mfaEmail") {
      this.mfaOtpError = null;
      this.mfaOtpSent = false;
      if (this.mfaIntervalId) {
        clearInterval(this.mfaIntervalId);
        this.mfaIntervalId = null;
      }
      this.mfaTimerSeconds = 0;
    }
  }

  getMfaEmailDisplay(): string {
    const email = this.formValues["mfaEmail"];
    if (!email || !this.mfaPreloaded) return email || "";
    return maskEmail(email);
  }

  getMfaMobileDisplay(): string {
    const mobile = this.formValues["mfaMobile"];
    if (!mobile || !this.mfaPreloaded) return mobile || "";
    return maskMobile(mobile);
  }

  onMfaMobileInputChange(value: any) {
    this.onMfaFieldChange("mfaMobile");
    if (!this.mfaPreloaded) this.formValues["mfaMobile"] = value;
  }

  onMfaEmailInputChange(value: any) {
    this.onMfaFieldChange("mfaEmail");
    if (!this.mfaPreloaded) this.formValues["mfaEmail"] = value;
  }

  handleButtonClick(event: any) {
    const fields = this.signUpData?.forms?.[this.activeForm]?.fields || [];
    switch (event.action) {
      case "goBack":
        this.goBack();
        break;

      case "submitForm":
        if (this.currentStep >= fields.length - 1) {
          return;
        }
        this.advanceStep();
        return;

      default:
        console.log(`WARN: Unhandled button action: ${event.action}`);
        break;
    }
  }

  onButtonClick(btn: any) {
    if (btn.action === "sendOtp") {
      this.sendOtp();
      return;
    }

    if (btn.action === "sendMfaOtp") {
      this.sendMfaOtp();
      return;
    }

    if (btn.action === "verifyMfaOtp") {
      this.verifyMfaOtp();
      return;
    }

    if (btn.action === "goBack") {
      this.goBack();
      return;
    }

    if (btn.action === "submitForm") {
      const fields = this.signUpData?.forms?.[this.activeForm]?.fields || [];
      let lastInteractiveIndex = -1;
      for (let i = 0; i < fields.length; i++) {
        if (fields[i].type !== "checkbox") lastInteractiveIndex = i;
      }
      if (this.currentStep >= lastInteractiveIndex) {
        this.onSubmit();
        return;
      }
      this.advanceStep();
      return;
    }

    this.handleButtonClick(btn);
  }

  getButtonText(btnOrAction: any): string {
    const action =
      typeof btnOrAction === "string"
        ? btnOrAction
        : (btnOrAction && btnOrAction.action) || "";
    switch (action) {
      case "sendOtp":
        return (btnOrAction && btnOrAction.text) || "Send OTP";
      case "sendMfaOtp":
        return "Send OTP";
      case "verifyMfaOtp":
        return "Verify OTP";
      case "resendMfa":
        return this.mfaTimerSeconds > 0
          ? "Resend (" + this.formatSecondsToMmSs(this.mfaTimerSeconds) + ")"
          : "Resend";
      case "resendActivation":
        return this.activationTimerSeconds > 0
          ? "Resend (" +
          this.formatSecondsToMmSs(this.activationTimerSeconds) +
          ")"
          : "Resend";
      case "goBack":
      case "cancelMfaInitial":
      case "cancelMfa":
        return "Back";
      default:
        if (typeof btnOrAction === "object" && btnOrAction && btnOrAction.text)
          return btnOrAction.text;
        return "";
    }

    // Format seconds into MM:SS for display inside buttons
  }

  private formatSecondsToMmSs(seconds: number): string {
    try {
      const s = Math.max(0, Number(seconds) || 0);
      const mm = Math.floor(s / 60);
      const ss = s % 60;
      return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
    } catch (e) {
      return "00:00";
    }
  }

  isButtonDisabled(btnOrAction: any): boolean {
    const action =
      typeof btnOrAction === "string"
        ? btnOrAction
        : (btnOrAction && btnOrAction.action) || "";
    if (action === "resendMfa")
      return this.mfaTimerSeconds > 0 || this.isMfaOtpLoading;
    if (action === "sendMfaOtp") return this.isMfaOtpLoading;
    if (action === "resendActivation") return this.activationTimerSeconds > 0;
    if (
      typeof btnOrAction === "object" &&
      btnOrAction &&
      btnOrAction.class === "submit-btn"
    )
      return this.isSubmitting;
    return false;
  }

  getButtonDef(action: string, formKey: string = this.activeForm): any {
    try {
      const form =
        this.signUpData?.forms?.[formKey] ||
        this.signUpData?.forms?.["mfaLoginForm"];
      if (!form || !form.buttons) return null;
      return form.buttons.find((b: any) => b.action === action) || null;
    } catch (e) {
      return null;
    }
  }

  getButtonClass(action: string, formKey?: string): string {
    const def = this.getButtonDef(action, formKey || this.activeForm);
    return def?.class || "";
  }

  getButtonIcon(action: string, formKey?: string): string | null {
    const def = this.getButtonDef(action, formKey || this.activeForm);
    return def?.icon || null;
  }

  startMfaTimer(seconds: number) {
    if (this.mfaIntervalId) {
      clearInterval(this.mfaIntervalId);
      this.mfaIntervalId = null;
    }
    const durationMs = Math.max(0, Number(seconds) || 0) * 1000;
    this.mfaTimerEnd = Date.now() + durationMs;
    this.mfaTimerSeconds = Math.ceil(durationMs / 1000);
    this.updateMfaCountdownAccurate();
    this.mfaIntervalId = setInterval(() => {
      this.updateMfaCountdownAccurate();
    }, 500);
  }

  private updateMfaCountdownAccurate() {
    if (!this.mfaTimerEnd) {
      this.mfaTimerSeconds = 0;
      return;
    }
    const remaining = this.mfaTimerEnd - Date.now();
    if (remaining <= 0) {
      this.mfaTimerSeconds = 0;
      if (this.mfaIntervalId) {
        clearInterval(this.mfaIntervalId);
        this.mfaIntervalId = null;
      }
      this.mfaTimerEnd = null;
      return;
    }
    this.mfaTimerSeconds = Math.ceil(remaining / 1000);
  }

  stopMfaTimer() {
    if (this.mfaIntervalId) {
      clearInterval(this.mfaIntervalId);
      this.mfaIntervalId = null;
    }
    this.mfaTimerEnd = null;
    this.mfaTimerSeconds = 0;
  }

  resendMfa() {
    if (this.mfaTimerSeconds > 0) return;
    const method = this.formValues["mfaMethod"];
    if (!method) {
      this.mfaOtpError = "Please select a verification method";
      return;
    }

    const userId =
      this.formValues["userId"] || this.formValues["tinNumber"] || null;
    if (!userId) {
      this.mfaOtpError = "Missing user identification for verification";
      return;
    }

    const contact =
      method === "mobile"
        ? this.formValues["mfaMobile"]
        : this.formValues["mfaEmail"];
    if (!contact) {
      this.mfaOtpError =
        method === "mobile"
          ? "Please enter your mobile number"
          : "Please enter a valid email address";
      return;
    }

    this.isMfaOtpLoading = true;
    this.mfaOtpToastId = this.alertService.show("Sending OTP, please wait…", {
      type: "info",
      duration: 6000,
    });
    this.authService.resendOtp(userId, contact as string, method).subscribe({
      next: (resp: any) => {
        this.isMfaOtpLoading = false;
        try {
          if (this.mfaOtpToastId) {
            this.alertService.remove(this.mfaOtpToastId);
            this.mfaOtpToastId = null;
          }
        } catch (e) {
          /* ignore */
        }
        const data = resp?.response || {};
        const sent = data?.sent === true || data?.sent === "true";
        const methodType = data?.method || method;
        if (sent) {
          const maskedContact = maskContact(contact as string);
          try {
            this.optkey = data?.otpKey || this.optkey;
          } catch (e) {
            /* ignore */
          }
          this.mfaOtpSent = true;
          this.alertService.show(`OTP sent to ${methodType} ${maskedContact}`, {
            type: "success",
          });
          this.startMfaTimer(60);
        } else {
          this.alertService.show(data?.message || "Failed to resend OTP", {
            type: "error",
          });
        }
      },
      error: (err: any) => {
        this.isMfaOtpLoading = false;
        try {
          if (this.mfaOtpToastId) {
            this.alertService.remove(this.mfaOtpToastId);
            this.mfaOtpToastId = null;
          }
        } catch (e) {
          /* ignore */
        }
        this.alertService.show(err?.error?.message || "Failed to resend OTP", {
          type: "error",
        });
      },
    });
  }

  cancelMfa() {
    this.mfaOtpSent = false;
    this.mfaOtpError = null;
    this.mfaTimerSeconds = 0;
    if (this.mfaIntervalId) {
      clearInterval(this.mfaIntervalId);
      this.mfaIntervalId = null;
    }
    this.formValues["mfaOtp"] = "";
    // Reset captcha state
    this.captchaError = null;
    this.captchaCode = "";
    try {
      this.captchaControl.setValue("");
      this.captchaControl.setErrors(null);
    } catch (e) {
      /* ignore */
    }
    setTimeout(() => {
      if (this.formValues["mfaMethod"] === "mobile") {
        const el = document.getElementById(
          "mfa-mobile-field",
        ) as HTMLInputElement | null;
        if (el) {
          el.focus();
          return;
        }
      }
      if (this.formValues["mfaMethod"] === "email") {
        const el = document.getElementById(
          "mfa-email-field",
        ) as HTMLInputElement | null;
        if (el) {
          el.focus();
          return;
        }
      }
      const radio = document.querySelector(
        "#mfa-method-mobile",
      ) as HTMLInputElement | null;
      if (radio) radio.focus();
    }, 50);
  }

  cancelMfaInitial() {
    // console.log('INFO: Cancel MFA initial', null, 'sign-up.component.ts');
    this.pendingLoginResponse = null;
    this.pendingPassword = null;
    this.mfaOtpSent = false;
    this.mfaOtpError = null;
    this.mfaTimerSeconds = 0;
    if (this.mfaIntervalId) {
      clearInterval(this.mfaIntervalId);
      this.mfaIntervalId = null;
    }
    this.formValues["mfaOtp"] = "";
    this.formValues["mfaEmail"] = "";
    this.formValues["mfaMobile"] = "";
    // Reset captcha state
    this.captchaError = null;
    this.captchaCode = "";
    try {
      this.captchaControl.setValue("");
      this.captchaControl.setErrors(null);
    } catch (e) {
      /* ignore */
    }
    this.formValues = {};
    this.setActiveForm("signupForm");
  }

  sendMfaOtp() {
    const method = (this.formValues["mfaMethod"] || "")
      .toString()
      .toLowerCase();
    this.mfaOtpError = null;
    if (!method) {
      this.mfaOtpError = "Please select a verification method";
      setTimeout(() => {
        const el = document.getElementById(
          "mfa-method-mobile",
        ) as HTMLInputElement | null;
        if (el) el.focus();
      }, 50);
      return;
    }

    let contact: string | null = null;
    let focusId: string | null = null;
    if (method === "mobile") {
      const mobile = this.formValues["mfaMobile"];
      if (!mobile || (typeof mobile === "string" && mobile.trim() === "")) {
        this.mfaOtpError = "Please enter your mobile number";
        focusId = "mfa-mobile-field";
      } else {
        const norm = String(mobile).replace(/[^0-9+]/g, "");
        if (!/^\+?[0-9]{6,15}$/.test(norm)) {
          this.mfaOtpError = "Please enter a valid mobile number";
          focusId = "mfa-mobile-field";
        } else {
          contact = mobile;
          focusId = "mfa-mobile-field";
        }
      }
    } else if (method === "email") {
      const email = this.formValues["mfaEmail"];
      const emailValidation = ValidatorService.emailValidator()({
        value: email,
      } as any);
      if (!email || emailValidation) {
        this.mfaOtpError = "Please enter a valid email address";
        focusId = "mfa-email-field";
      } else {
        contact = email;
        focusId = "mfa-email-field";
      }
    } else {
      this.mfaOtpError = "Unknown verification method";
      return;
    }

    if (this.mfaOtpError) {
      if (focusId)
        setTimeout(() => {
          const el = document.getElementById(
            focusId,
          ) as HTMLInputElement | null;
          if (el) el.focus();
        }, 50);
      return;
    }

    const userId =
      this.formValues["userId"] || this.formValues["tinNumber"] || null;
    if (!userId) {
      this.mfaOtpError = "Missing user identification for verification";
      return;
    }

    this.isMfaOtpLoading = true;
    this.mfaOtpToastId = this.alertService.show("Sending OTP, please wait…", {
      type: "info",
      duration: 6000,
    });
    this.authService
      .verifyContact(userId, contact as string, method)
      .subscribe({
        next: (resp: any) => {
          this.isMfaOtpLoading = false;
          try {
            if (this.mfaOtpToastId) {
              this.alertService.remove(this.mfaOtpToastId);
              this.mfaOtpToastId = null;
            }
          } catch (e) {
            /* ignore */
          }
          const data = resp?.response || {};
          console.log(
            "INFO: verifyContact response",
            data,
            "sign-up.component.ts",
          );
          const match = data?.match === true || (data && data.match === "true");
          if (!match) {
            this.mfaOtpError =
              method === "mobile"
                ? "The mobile number does not match our records for this user"
                : "The email address does not match our records for this user";
            return;
          }
          this.mfaOtpError = null;
          this.mfaOtpSent = true;
          this.optkey = data?.otpKey || "";
          const maskedContact =
            method === "mobile"
              ? maskMobile(contact as string)
              : maskEmail(contact as string);
          if (method === "mobile") {
            this.startMfaTimer(60);
            this.alertService.show(
              `OTP sent to mobile number ${maskedContact}`,
              { type: "success", duration: 3000 },
            );
          } else {
            this.startMfaTimer(60);
            this.alertService.show(
              `OTP sent to email address ${maskedContact}`,
              { type: "success", duration: 3000 },
            );
          }

          this.focusOtpInput();
          try {
            setTimeout(() => this.generateCaptcha(), 50);
          } catch (e) {
            /* ignore */
          }
        },
        error: (err: any) => {
          this.isMfaOtpLoading = false;
          try {
            if (this.mfaOtpToastId) {
              this.alertService.remove(this.mfaOtpToastId);
              this.mfaOtpToastId = null;
            }
          } catch (e) {
            /* ignore */
          }
          this.logger.error(
            "verifyContact failed",
            err,
            "sign-up.component.ts",
          );
          const msg =
            err?.error?.message ||
            err?.message ||
            (typeof err === "string" ? err : null) ||
            "Unable to verify contact";
          this.mfaOtpError = msg;
          if (focusId)
            setTimeout(() => {
              const el = document.getElementById(
                focusId,
              ) as HTMLInputElement | null;
              if (el) el.focus();
            }, 50);
        },
      });
  }

  verifyMfaOtp() {
    const otp = this.formValues["mfaOtp"];
    if (!otp) {
      this.mfaOtpError = "OTP is required";
      this.focusOtpInput();
      this.clearSignupOtpInputs();
      return;
    }
    // Ensure OTP is full length before running captcha validation
    const otpStr = String(otp || "");
    if (otpStr.length < this.inlineOtpLength) {
      this.mfaOtpError = "OTP is required";
      this.focusOtpInput();
      this.clearSignupOtpInputs();
      return;
    }
    // Validate captcha first only if this form requires captcha
    if (this.activeFormHasCaptcha()) {
      // If no captcha code has been generated yet, prompt user to enter captcha
      if (!this.captchaCode || this.captchaCode.trim() === "") {
        this.captchaError = "CAPTCHA is required";
        this.refreshCaptcha();
        setTimeout(() => {
          const inp = document.getElementById(
            "signup-captcha-input",
          ) as HTMLInputElement | null;
          if (inp) inp.focus();
        }, 50);
        return;
      }

      // If input is empty, prompt accordingly
      const rawVal =
        this.captchaControl && this.captchaControl.value != null
          ? String(this.captchaControl.value).trim()
          : "";
      if (!rawVal) {
        this.captchaError = "CAPTCHA is required";
        this.refreshCaptcha();
        setTimeout(() => {
          const inp = document.getElementById(
            "signup-captcha-input",
          ) as HTMLInputElement | null;
          if (inp) inp.focus();
        }, 50);
        return;
      }

      const ok = this.verifyCaptcha();
      if (!ok) {
        this.captchaError = "Incorrect CAPTCHA";
        this.refreshCaptcha();
        setTimeout(() => {
          const inp = document.getElementById(
            "signup-captcha-input",
          ) as HTMLInputElement | null;
          if (inp) inp.focus();
        }, 50);
        return; // stop here, do not verify OTP with server
      } else {
        this.captchaError = null;
      }
    }

    const userId =
      this.formValues["userId"] || this.formValues["tinNumber"] || null;
    const method = this.formValues["mfaMethod"] || null;
    if (!userId) {
      this.mfaOtpError = "Missing user identification for verification";
      return;
    }

    // localStorage.setItem("testbin", userId);
    // console.log(
    //   "localstorage testbin",
    //   this.encryptionService.decrypt(
    //     localStorage.getItem("testbin")?.toString() || "",
    //   ),
    // );
    this.loaderService.show("Verifying...");

    this.authService
      .verifyMfaOtp(
        userId,
        otp,
        method,
        this.optkey,
        this.pendingPassword || undefined,
      )
      .subscribe({
        next: (resp: any) => {
          try {
            const data = resp?.response || {};
            localStorage.setItem("bin", resp?.response.user.userId);
            const verified =
              data?.verified === true || (data && data.verified === "true");
            console.log(
              "INFO: verifyMfaOtp response",
              data,
              "sign-up.component.ts",
            );
            if (!verified) {
              this.loaderService.hide();
              this.mfaOtpError = "Invalid OTP. Please try again.";
              try {
                this.clearSignupOtpInputs();
              } catch (e) {
                /* ignore */
              }
              return;
            }

            try {
              const remember = !!this.formValues["rememberPassword"];
              // Prefer token returned by verifyMfaOtp response (backend issues JWT after MFA)
              const tokenResponse = resp;
              const tokenData = tokenResponse?.response || {};
              if (tokenData && tokenData.token) {
                try {
                  this.authService.finalizeLogin(tokenResponse, { remember });
                } catch (e) {
                  console.warn(
                    "Failed to finalize login with token from verifyMfaOtp",
                    e,
                  );
                }
              }

              try {
                this.persistSavedLoginPreference(remember, userId);
              } catch (e) {
                console.log("WARN: Unable to handle saved credentials", e);
              }

              this.mfaOtpError = null;
              this.mfaOtpSent = false;
              this.pendingPassword = null;
              this.pendingLoginResponse = null;
              if (this.mfaIntervalId) {
                clearInterval(this.mfaIntervalId);
                this.mfaIntervalId = null;
              }
              this.loaderService.hide();
              try {
                const svc = (
                  this.currentService ||
                  (typeof sessionStorage !== "undefined"
                    ? sessionStorage.getItem("signup_service")
                    : null) ||
                  ""
                )
                  .toString()
                  .toUpperCase();
                if (svc === "DMTT") {
                  console.log("In dmtt");
                  const userObj = tokenData?.user || data?.user || {};
                  const userProfile: any =
                    this.tokenService?.getUserProfile?.() || {};
                  const profileFromOtp = {
                    binNumber:
                      userObj?.binNumber ||
                      userObj?.userId ||
                      userObj?.TIN ||
                      userObj?.tin ||
                      userProfile?.tin ||
                      userProfile?.id ||
                      this.formValues["userId"] ||
                      this.formValues["tinNumber"] ||
                      userId ||
                      "",
                    email:
                      userObj?.email ||
                      userProfile?.email ||
                      this.formValues["mfaEmail"] ||
                      null,
                    persona:
                      userObj?.persona ||
                      userObj?.profiles ||
                      userObj?.role ||
                      userProfile?.role ||
                      userProfile?.profile ||
                      [],
                    tinNumber:
                      userObj?.tinNumber ||
                      userObj?.TIN ||
                      userObj?.tin ||
                      userProfile?.tin ||
                      null,
                    taxType: userObj?.taxType || userObj?.tax_type || null,
                    firstName: userObj?.firstName || userObj?.firstname || null,
                    lastName: userObj?.lastName || userObj?.lastname || null,
                    mobileNumber:
                      userObj?.mobileNumber ||
                      userObj?.mobile ||
                      userObj?.phone ||
                      userProfile?.phone ||
                      this.formValues["mfaMobile"] ||
                      null,
                    isFirstTimeLogin: userObj?.isFirstTimeLogin || null,
                    applicationId:
                      userObj?.applicationId ||
                      userObj?.applicationid ||
                      userObj?.userId ||
                      "",
                    registeredName:
                      userObj?.registeredName || userObj?.registered_name || "",
                  };

                  const payload = {
                    token: tokenData?.token || data?.token || "",
                    profile: profileFromOtp,
                  };

                  const encryptedPayload =
                    this.encryptionService.encrypt(payload);
                  const target = environment.dmttDashboardUrl;
                  window.location.href = `${target}?dmtt_token=${encodeURIComponent(encryptedPayload)}`;
                } else if (svc === "VAT") {
                  const userObj = tokenData?.user || data?.user || {};
                  const userProfile: any =
                    this.tokenService?.getUserProfile?.() || {};
                  const profileFromOtp = {
                    binNumber:
                      userObj?.binNumber ||
                      userObj?.userId ||
                      userObj?.TIN ||
                      userObj?.tin ||
                      userProfile?.tin ||
                      userProfile?.id ||
                      this.formValues["userId"] ||
                      this.formValues["tinNumber"] ||
                      userId ||
                      "",
                    email:
                      userObj?.email ||
                      userProfile?.email ||
                      this.formValues["mfaEmail"] ||
                      null,
                    persona:
                      userObj?.persona ||
                      userObj?.profiles ||
                      userObj?.role ||
                      userProfile?.role ||
                      userProfile?.profile ||
                      [],
                    tinNumber:
                      userObj?.tinNumber ||
                      userObj?.TIN ||
                      userObj?.tin ||
                      userProfile?.tin ||
                      null,
                    taxType: userObj?.taxType || userObj?.tax_type || null,
                    firstName: userObj?.firstName || userObj?.firstname || null,
                    lastName: userObj?.lastName || userObj?.lastname || null,
                    mobileNumber:
                      userObj?.mobileNumber ||
                      userObj?.mobile ||
                      userObj?.phone ||
                      userProfile?.phone ||
                      this.formValues["mfaMobile"] ||
                      null,
                    isFirstTimeLogin: userObj?.isFirstTimeLogin || null,
                    applicationId:
                      userObj?.applicationId ||
                      userObj?.applicationid ||
                      userObj?.userId ||
                      "",
                    registeredName:
                      userObj?.registeredName || userObj?.registered_name || "",
                  };

                  const payload = {
                    token: tokenData?.token || data?.token || "",
                    profile: profileFromOtp,
                  };

                  const encryptedPayload =
                    this.encryptionService.encrypt(payload);
                  const target = environment.vatDashboardUrl;
                  window.location.href = `${target}?token=${encodeURIComponent(encryptedPayload)}`;
                } else if (svc === "BL") {
                  const userObj = tokenData?.user || data?.user || {};
                  const userProfile: any =
                    this.tokenService?.getUserProfile?.() || {};
                  const profileFromOtp = {
                    binNumber:
                      userObj?.binNumber ||
                      userObj?.userId ||
                      userObj?.TIN ||
                      userObj?.tin ||
                      userProfile?.tin ||
                      userProfile?.id ||
                      this.formValues["userId"] ||
                      this.formValues["tinNumber"] ||
                      userId ||
                      "",
                    email:
                      userObj?.email ||
                      userProfile?.email ||
                      this.formValues["mfaEmail"] ||
                      null,
                    persona:
                      userObj?.persona ||
                      userObj?.profiles ||
                      userObj?.role ||
                      userProfile?.role ||
                      userProfile?.profile ||
                      [],
                    tinNumber:
                      userObj?.tinNumber ||
                      userObj?.TIN ||
                      userObj?.tin ||
                      userProfile?.tin ||
                      null,
                    taxType: userObj?.taxType || userObj?.tax_type || null,
                    firstName: userObj?.firstName || userObj?.firstname || null,
                    lastName: userObj?.lastName || userObj?.lastname || null,
                    mobileNumber:
                      userObj?.mobileNumber ||
                      userObj?.mobile ||
                      userObj?.phone ||
                      userProfile?.phone ||
                      this.formValues["mfaMobile"] ||
                      null,
                    isFirstTimeLogin: userObj?.isFirstTimeLogin || null,
                    applicationId:
                      userObj?.applicationId ||
                      userObj?.applicationid ||
                      userObj?.userId ||
                      "",
                    registeredName:
                      userObj?.registeredName || userObj?.registered_name || "",
                  };

                  const payload = {
                    token: tokenData?.token || data?.token || "",
                    profile: profileFromOtp,
                  };

                  const encryptedPayload =
                    this.encryptionService.encrypt(payload);
                  const target = environment.blDashboardUrl;
                  window.location.href = `${target}?token=${encodeURIComponent(encryptedPayload)}`;
                }
                else {
                  this.authService.markMfaVerified();
                  this.router.navigate(["/dashboard"]);
                }
              } catch (e) {
                this.authService.markMfaVerified();
                this.router.navigate(["/dashboard"]);
              }
            } catch (e) {
              this.loaderService.hide();
              this.logger.error(
                "Error finalizing login after MFA",
                e,
                "sign-up.component.ts",
              );
              this.mfaOtpError = "Unable to complete login at this time";
            }
          } catch (e) {
            this.loaderService.hide();
            this.logger.error(
              "Error handling verifyMfaOtp response",
              e,
              "sign-up.component.ts",
            );
            this.mfaOtpError = "Unable to verify OTP at this time";
          }
        },
        error: (err: any) => {
          this.loaderService.hide();
          this.logger.error("verifyMfaOtp failed", err, "sign-up.component.ts");
          const msg =
            err?.error?.message ||
            err?.message ||
            (typeof err === "string" ? err : null) ||
            "Invalid OTP. Please try again.";
          this.mfaOtpError = msg;
          this.alertService.show(msg, { type: "error", duration: 4000 });
          try {
            this.clearSignupOtpInputs();
          } catch (e) {
            /* ignore */
          }
        },
      });
  }

  private clearSignupOtpInputs() {
    try {
      this.inlineMfaOtpBoxes = new Array(this.inlineOtpLength).fill("");
      this.formValues["mfaOtp"] = "";
      if (typeof document === "undefined") return;
      const inputs = document.querySelectorAll(
        ".otp-input-box input",
      ) as NodeListOf<HTMLInputElement>;
      for (let i = 0; i < inputs.length; i++) {
        const inp = inputs[i];
        if (inp) {
          inp.value = "";
          try {
            inp.dispatchEvent(new Event("input", { bubbles: true }));
          } catch (e) {
            /* ignore */
          }
        }
        const box = document.querySelectorAll(".otp-input-box")[i] as
          | HTMLElement
          | undefined;
        if (box) box.classList.remove("filled");
      }
    } catch (e) {
      // ignore
    }
  }

  goBack() {
    // console.log('INFO: Go back to home', null, 'sign-up.component.ts');
    this.formValues = {};
    this.showError = false;
    this.currentStep = 0;
    this.userIdNotFound = null;
    this.activationSent = false;
    this.activationMessage = null;
    this.stopActivationTimer();
    this.forgotPasswordInstructionsSent = false;
    this.isFirstTimeUser = false;
    // Reset captcha state
    this.captchaError = null;
    this.captchaCode = "";
    try {
      this.captchaControl.setValue("");
      this.captchaControl.setErrors(null);
    } catch (e) {
      /* ignore */
    }

    // if (this.activeForm && this.activeForm !== 'signupForm') {
    this.setActiveForm("signupForm");
    // return;
    // }

    this.router.navigate(["/"]);
  }

  advanceStep() {
    const fields = this.signUpData?.forms?.[this.activeForm]?.fields || [];
    if (!fields.length) return;
    let lastInteractiveIndex = -1;
    for (let i = 0; i < fields.length; i++) {
      if (fields[i].type !== "checkbox") lastInteractiveIndex = i;
    }

    if (this.currentStep < lastInteractiveIndex) {
      const currentField = fields[this.currentStep];
      const val = this.formValues[currentField.key];
      if (
        currentField.required &&
        (!val || (typeof val === "string" && val.trim() === ""))
      ) {
        this.showError = true;
        this.focusField(currentField.key);
        return;
      }

      if (this.currentStep === 0) {
        this.userIdNotFound = "";
        this.verifyingToastId = this.alertService.show("Verifying Bin", {
          type: "info",
        });
        const userId = val;
        if (userId && typeof userId === "string") {
          this.authService
            .checkUserId(userId, "First Time Password")
            .subscribe({
              next: (resp: any) => {
                console.log(
                  "INFO: checkUserId success",
                  resp,
                  "sign-up.component.ts",
                );

                // hide verifying toast as soon as response arrives
                try {
                  if (this.verifyingToastId) {
                    this.alertService.remove(this.verifyingToastId);
                    this.verifyingToastId = null;
                  }
                } catch (e) {
                  /* ignore */
                }
                try {
                  const data = resp?.response;
                  const exists = data?.exists === true;
                  const isFirstTime = data?.firstTimeUser;
                  // const isFirstTime = false;
                  const role = data?.role;

                  console.log(
                    "INFO: checkUserId response",
                    data,
                    "sign-up.component.ts",
                    role,
                  );
                  if (data?.registeredEmail)
                    this.formValues["registeredEmail"] = data.registeredEmail;
                  this.userName = data?.userName || "User";

                  if (!exists) {
                    this.showError = true;
                    this.userIdNotFound =
                      "The entered BIN does not match our records. Please enter a valid BIN";
                    this.activationSent = false;
                    this.accountLocked = false;
                    try {
                      this.focusField("userId", 50);
                    } catch (e) {
                      /* ignore */
                    }
                    return;
                  } else if (exists && isFirstTime) {
                    const emailDisplay = data?.registeredEmail
                      ? maskEmail(data.registeredEmail)
                      : "your registered email";
                    if (data?.registeredEmail)
                      this.formValues["registeredEmail"] = data.registeredEmail;
                    const linkTypeToSend = "First Time Password";
                    this.authService
                      .postUpdatePassword(this.formValues["userId"])
                      .subscribe({
                        next: (res: any) => {
                          const data = res?.response || {};
                          console.log(
                            "INFO: postUpdatePassword response",
                            data,
                            "sign-up.component.ts",
                          );
                          if (data?.flag) {
                            try {
                              let resetKey = data?.resetKey || "";
                              console.log(
                                "INFO: Received resetKey from postUpdatePassword",
                                resetKey,
                                "sign-up.component.ts",
                              );
                              this.authService
                                .resendMail(
                                  this.formValues["registeredEmail"],
                                  linkTypeToSend,
                                  this.userName,
                                  userId,
                                  resetKey,
                                  true,
                                )
                                .subscribe({
                                  next: (resp: any) => {
                                    const data = resp?.response || {};
                                    const sent =
                                      data?.sent === true ||
                                      data?.sent === "true";
                                    if (sent) {
                                      const toValue = data?.to || emailDisplay;
                                      const isEmail =
                                        typeof toValue === "string" &&
                                        toValue.includes("@");
                                      const display = isEmail
                                        ? maskEmail(toValue)
                                        : String(toValue);
                                    } else {
                                      this.alertService.show(
                                        data?.message ||
                                        "Failed to resend instructions",
                                        { type: "error", duration: 4000 },
                                      );
                                    }
                                  },
                                  error: (err: any) => {
                                    this.logger.error(
                                      "resendActivation resendMail failed",
                                      err,
                                      "sign-up.component.ts",
                                    );
                                    this.alertService.show(
                                      err?.error?.message ||
                                      "Unable to resend instructions",
                                      { type: "error", duration: 4000 },
                                    );
                                  },
                                });
                            } catch (e) {
                              this.logger.warn(
                                "Error handling postUpdatePassword success",
                                e,
                                "sign-up.component.ts",
                              );
                            }
                          } else {
                            // Backend indicated not to proceed
                            const msg =
                              data?.message ||
                              "Unable to process password reset request";
                            this.alertService.show(msg, {
                              type: "error",
                              duration: 4000,
                            });
                            this.focusField("userId");
                            return;
                          }
                        },
                        error: (err: any) => {
                          console.log(
                            "INFO: postUpdatePassword error",
                            err,
                            "sign-up.component.ts",
                          );
                          this.logger.error(
                            "postUpdatePassword failed",
                            err,
                            "sign-up.component.ts",
                          );
                          this.alertService.show(
                            err?.error?.message ||
                            "Unable to process password reset request",
                            { type: "error", duration: 4000 },
                          );
                          this.focusField("userId");
                          return;
                        },
                      });

                    this.activationMessage =
                      "First time user. Please follow the link sent to  " +
                      emailDisplay +
                      " to set the password.";
                    this.activationSent = true;
                    this.isFirstTimeUser = true;
                    this.startActivationTimer(60);
                    this.alertService.show(
                      "Mail has been sent to " +
                      emailDisplay +
                      " to set the password.",
                      { type: "info", duration: 6000 },
                    );
                    this.isSubmitting = false;
                    return;
                  }

                  if (this.router.url.includes("dmtt")) {
                    // if (resp?.response?.persona.includes('TXP') && resp?.response?.taxtype.toLowerCase().includes('individual')) {
                    // if (resp?.response?.taxtype.toLowerCase().includes('individual')) {
                    let resticTaxType = [
                      "Individual",
                      "Individual Resident",
                      "Individual Non-Resident",
                      "Entity Non-Resident",
                    ]; // Changed from includes('individual') to the names
                    if (resticTaxType.includes(resp?.response?.taxtype)) {
                      this.alertService.show(
                        "You are not authorized to login here.",
                        { type: "error", duration: 4000 },
                      );
                      return;
                    }
                  }

                  if (!role) {
                    this.alertService.show(
                      "You are not authorized to login here.",
                      { type: "error", duration: 4000 },
                    );
                    return;
                  }

                  // not a first-time user
                  this.isFirstTimeUser = false;
                  this.currentStep++;
                  this.showError = false;
                  setTimeout(() => {
                    const nextEl = document.getElementById(
                      "signup-field-" + this.currentStep,
                    ) as HTMLElement | null;
                    if (nextEl) nextEl.focus();
                    this.scrollToElementIfNeeded(nextEl);
                  }, 50);
                } catch (e) {
                  this.logger.warn(
                    "Error reading checkUserId response",
                    e,
                    "sign-up.component.ts",
                  );
                  this.alertService.show(
                    "Unable to verify UserId at this time.",
                    { type: "error", duration: 4000 },
                  );
                }
              },
              error: (err: any) => {
                // hide verifying toast on error
                try {
                  if (this.verifyingToastId) {
                    this.alertService.remove(this.verifyingToastId);
                    this.verifyingToastId = null;
                  }
                } catch (e) {
                  /* ignore */
                }
                console.log(
                  "INFO: checkUserId error",
                  err,
                  "sign-up.component.ts",
                );
                this.logger.error(
                  "checkUserId failed",
                  err,
                  "sign-up.component.ts",
                );
                this.alertService.show(
                  err?.error?.message || "Unable to verify UserId",
                  { type: "error", duration: 4000 },
                );
                this.focusField("userId");
              },
            });
          return;
        }
      }

      // Step advancement disabled - keep currentStep at 0 so userId remains editable
      /*
      this.currentStep++;
      this.showError = false;
 
      setTimeout(() => {
        const nextEl = document.getElementById('signup-field-' + this.currentStep) as HTMLElement | null;
        if (nextEl) nextEl.focus();
        this.scrollToElementIfNeeded(nextEl);
      }, 50);
      return;
      */
    }

    this.onSubmit();
  }

  resetKey: any = "";
  forgotPassword() {
    this.failedAttempts = 0;
    this.showError = false;
    const userId = this.formValues["userId"] || null;
    console.log(
      "INFO: Forgot password for userId/tin",
      this.formValues,
      userId,
      "sign-up.component.ts",
    );
    if (!userId) {
      this.showError = true;
      this.alertService.show(
        "Please enter your User ID before requesting a password reset.",
        { type: "error", duration: 4000 },
      );
      this.focusField("userId");
      return;
    }

    //call the API to post the reset the flag

    this.authService.postUpdatePassword(userId).subscribe({
      next: (res: any) => {
        const data = res?.response || {};
        console.log(
          "INFO: postUpdatePassword response",
          data,
          "sign-up.component.ts",
        );
        // Only proceed with sending reset instructions when backend explicitly allows it
        if (data?.flag) {
          try {
            this.isFirstTimeUser = false;
            this.resetKey = data?.resetKey || "";
            console.log(
              "INFO: Received resetKey from postUpdatePassword",
              this.resetKey,
              "sign-up.component.ts",
            );
            this.forgotPasswordInstructionsSent = true;
            const registeredEmail = this.formValues["registeredEmail"];
            this.activationSent = true;
            if (registeredEmail) {
              this.formValues["registeredEmail"] = registeredEmail;
              const maskedEmail = maskEmail(registeredEmail);
              this.activationMessage =
                "Follow the link sent to " +
                maskedEmail +
                " to reset your password.";
            } else {
              this.activationMessage =
                "The entered BIN does not match our records. Please enter a valid BIN";
            }

            this.startActivationTimer(60);
            const toResend =
              registeredEmail || this.formValues["registeredEmail"] || null;
            if (toResend) {
              this.authService
                .resendMail(
                  toResend,
                  "Reset Password",
                  this.userName,
                  userId,
                  this.resetKey,
                  false,
                )
                .subscribe({
                  next: (r: any) => {
                    const d = r?.response || {};
                    const ok = d?.sent === true || d?.sent === "true";
                    if (ok) {
                      const maskedEmail = maskEmail(d?.to || toResend);
                      this.alertService.show(
                        "Reset instructions sent to " + maskedEmail,
                        { type: "success", duration: 4000 },
                      );
                    }
                  },
                  error: (e: any) => {
                    this.logger.warn(
                      "forgotPassword resendMail failed",
                      e,
                      "sign-up.component.ts",
                    );
                  },
                });
            }
          } catch (e) {
            this.logger.warn(
              "Error handling postUpdatePassword success",
              e,
              "sign-up.component.ts",
            );
          }
        } else {
          // Backend indicated not to proceed
          const msg =
            data?.message || "Unable to process password reset request";
          this.alertService.show(msg, { type: "error", duration: 4000 });
          this.focusField("userId");
          return;
        }
      },
      error: (err: any) => {
        console.log(
          "INFO: postUpdatePassword error",
          err,
          "sign-up.component.ts",
        );
        this.logger.error(
          "postUpdatePassword failed",
          err,
          "sign-up.component.ts",
        );
        this.alertService.show(
          err?.error?.message || "Unable to process password reset request",
          { type: "error", duration: 4000 },
        );
        this.focusField("userId");
        return;
      },
    });

    // this.alertService.show('Verifying User ID', { type: 'info' });
  }

  sendOtp() {
    const userId = this.formValues["userId"] || null;
    const val = this.formValues["registeredEmail"];
    const targetKey = "registeredEmail";
    if (!val || typeof val !== "string" || val.trim() === "") {
      this.emailFieldErrors[targetKey] =
        "Please enter a registered email address.";
      this.focusField(targetKey);
      return;
    }
    const emailValidation = ValidatorService.emailValidator()({
      value: val,
    } as any);
    if (emailValidation) {
      this.emailFieldErrors[targetKey] =
        "Please enter a valid registered email address.";
      this.focusField(targetKey);
      return;
    }

    if (!userId) {
      this.alertService.show(
        "UserId is required to verify the registered email.",
        { type: "error", duration: 4000 },
      );
      this.focusField("userId");
      return;
    }

    this.focusOtpInput();
  }

  toggleVisibility(index: number) {
    const fields = this.signUpData?.forms?.[this.activeForm]?.fields || [];
    if (!fields[index]) return;
    fields[index].hide = !fields[index].hide;
  }

  async onSubmit(): Promise<void> {
    const fields = this.signUpData?.forms?.[this.activeForm]?.fields || [];
    for (let field of fields) {
      if (field.required) {
        const val = this.formValues[field.key];
        if (!val || (typeof val === "string" && val.trim() === "")) {
          this.showError = true;
          this.focusField(field.key);
          return;
        }
      }
    }

    this.showError = false;
    if (this.isSubmitting) return;
    this.isSubmitting = true;

    const userIdKey = fields[0]?.key || "userId";
    const loginId = this.formValues[userIdKey];

    this.authService.login(loginId, this.formValues["password"]).subscribe({
      next: (response) => {
        let toastMsg = "Logged in successfully";
        localStorage.setItem("bin", loginId);
        let dataResponse: any = response?.response;
        try {
          if (typeof dataResponse === "string" && dataResponse.includes(":")) {
            dataResponse = this.encryptionService.decrypt(dataResponse);
          }
          toastMsg =
            dataResponse?.message ||
            dataResponse?.response?.message ||
            toastMsg;

          const backendStatus =
            dataResponse?.status ||
            dataResponse?.response?.status ||
            dataResponse?.success;
          const lcMsg = (toastMsg || "").toString().toLowerCase();
          const indicatesError =
            backendStatus === "error" ||
            backendStatus === "failed" ||
            backendStatus === false ||
            lcMsg.includes("invalid") ||
            lcMsg.includes("failed") ||
            lcMsg.includes("incorrect");
          if (indicatesError) {
            this.alertService.show(toastMsg || "Login failed", {
              type: "error",
              duration: 5000,
            });
            this.showError = true;
            this.failedAttempts++;
            this.isSubmitting = false;
            return;
          }
        } catch (e) {
          this.logger.warn(
            "Unable to decrypt or read login message from response",
            e,
            "sign-up.component.ts",
          );
        }

        this.formValues["userId"] = loginId;

        try {
          const svc = (
            this.currentService ||
            (typeof sessionStorage !== "undefined"
              ? sessionStorage.getItem("signup_service")
              : null) ||
            ""
          )
            .toString()
            .toUpperCase();
          if (svc === "DMTT") {
            console.log("In dmtt");
            const userObj = dataResponse.user || {};
            const userProfile: any =
              this.tokenService?.getUserProfile?.() || {};
            const profileFromOtp = {
              binNumber: userObj?.binNumber || userObj?.userId || "",
              email:
                userObj?.email ||
                userProfile?.email ||
                this.formValues["mfaEmail"] ||
                null,
              persona:
                userObj?.persona ||
                userObj?.profiles ||
                userObj?.role ||
                userProfile?.role ||
                userProfile?.profile ||
                [],
              tinNumber:
                userObj?.tinNumber ||
                userObj?.TIN ||
                userObj?.tin ||
                userProfile?.tin ||
                null,
              taxType: userObj?.taxType || userObj?.tax_type || null,
              firstName: userObj?.firstName || userObj?.firstname || null,
              lastName: userObj?.lastName || userObj?.lastname || null,
              mobileNumber:
                userObj?.mobileNumber ||
                userObj?.mobile ||
                userObj?.phone ||
                userProfile?.phone ||
                this.formValues["mfaMobile"] ||
                null,
              isFirstTimeLogin: userObj?.isFirstTimeLogin || null,
              applicationId:
                userObj?.applicationId ||
                userObj?.applicationid ||
                userObj?.userId ||
                "",
              registeredName:
                userObj?.registeredName || userObj?.registered_name || "",
            };

            const payload = {
              token: dataResponse.token || "",
              profile: profileFromOtp,
            };

            const encryptedPayload = this.encryptionService.encrypt(payload);
            const target = environment.dmttDashboardUrl;
            window.location.href = `${target}?dmtt_token=${encodeURIComponent(encryptedPayload)}`;
          } else if (svc === "VAT") {
            const userObj = dataResponse.user || {};
            const userProfile: any =
              this.tokenService?.getUserProfile?.() || {};
            const profileFromOtp = {
              binNumber: userObj?.binNumber || userObj?.userId || "",
              email:
                userObj?.email ||
                userProfile?.email ||
                this.formValues["mfaEmail"] ||
                null,
              persona:
                userObj?.persona ||
                userObj?.profiles ||
                userObj?.role ||
                userProfile?.role ||
                userProfile?.profile ||
                [],
              tinNumber:
                userObj?.tinNumber ||
                userObj?.TIN ||
                userObj?.tin ||
                userProfile?.tin ||
                null,
              taxType: userObj?.taxType || userObj?.tax_type || null,
              firstName: userObj?.firstName || userObj?.firstname || null,
              lastName: userObj?.lastName || userObj?.lastname || null,
              mobileNumber:
                userObj?.mobileNumber ||
                userObj?.mobile ||
                userObj?.phone ||
                userProfile?.phone ||
                this.formValues["mfaMobile"] ||
                null,
              isFirstTimeLogin: userObj?.isFirstTimeLogin || null,
              applicationId:
                userObj?.applicationId ||
                userObj?.applicationid ||
                userObj?.userId ||
                "",
              registeredName:
                userObj?.registeredName || userObj?.registered_name || "",
            };

            const payload = {
              token: dataResponse.token || "",
              profile: profileFromOtp,
            };

            const encryptedPayload = this.encryptionService.encrypt(payload);
            const target = environment.vatDashboardUrl;
            window.location.href = `${target}?token=${encodeURIComponent(encryptedPayload)}`;
          } else if (svc === "BL") {
            const userObj = dataResponse.user || {};
            const userProfile: any =
              this.tokenService?.getUserProfile?.() || {};
            const profileFromOtp = {
              binNumber: userObj?.binNumber || userObj?.userId || "",
              email:
                userObj?.email ||
                userProfile?.email ||
                this.formValues["mfaEmail"] ||
                null,
              persona:
                userObj?.persona ||
                userObj?.profiles ||
                userObj?.role ||
                userProfile?.role ||
                userProfile?.profile ||
                [],
              tinNumber:
                userObj?.tinNumber ||
                userObj?.TIN ||
                userObj?.tin ||
                userProfile?.tin ||
                null,
              taxType: userObj?.taxType || userObj?.tax_type || null,
              firstName: userObj?.firstName || userObj?.firstname || null,
              lastName: userObj?.lastName || userObj?.lastname || null,
              mobileNumber:
                userObj?.mobileNumber ||
                userObj?.mobile ||
                userObj?.phone ||
                userProfile?.phone ||
                this.formValues["mfaMobile"] ||
                null,
              isFirstTimeLogin: userObj?.isFirstTimeLogin || null,
              applicationId:
                userObj?.applicationId ||
                userObj?.applicationid ||
                userObj?.userId ||
                "",
              registeredName:
                userObj?.registeredName || userObj?.registered_name || "",
            };

            const payload = {
              token: dataResponse.token || "",
              profile: profileFromOtp,
            };

            const encryptedPayload = this.encryptionService.encrypt(payload);
            const target = environment.blDashboardUrl;
            window.location.href = `${target}?token=${encodeURIComponent(encryptedPayload)}`;
          }
          else {
            this.authService.markMfaVerified();
            this.router.navigate(["/dashboard"]);
          }
        } catch (e) {
          this.authService.markMfaVerified();
          this.router.navigate(["/dashboard"]);
        }
        try {
          const contact =
            dataResponse?.contact ||
            (dataResponse?.user
              ? {
                email: dataResponse.user.email,
                mobile: dataResponse.user.mobilenumber,
              }
              : null) ||
            (dataResponse?.response?.user
              ? {
                email: dataResponse.response.user.email,
                mobile: dataResponse.response.user.mobilenumber,
              }
              : null);
          if (contact) {
            if (contact.email) this.formValues["mfaEmail"] = contact.email;
            if (contact.mobile) this.formValues["mfaMobile"] = contact.mobile;
            if (contact.email || contact.mobile) this.mfaPreloaded = true;
          }
        } catch (e) {
          this.logger.error("Unexpected error", e, "sign-up.component.ts");
        }
        this.isSubmitting = false;

        const loginPayload = dataResponse?.token
          ? dataResponse
          : dataResponse?.response || null;
        if (
          loginPayload &&
          loginPayload.token &&
          dataResponse?.mfaPending !== true
        ) {
          const remember = !!this.formValues["rememberPassword"];
          // Compute service early to avoid finalizing login into OTB when redirecting to DMTT
          const svc = (
            this.currentService ||
            (typeof sessionStorage !== "undefined"
              ? sessionStorage.getItem("signup_service")
              : null) ||
            ""
          )
            .toString()
            .toUpperCase();
          if (svc !== "DMTT" && svc !== "VAT" && svc !== "BL") {
            this.authService.finalizeLogin(response, { remember });
            try {
              this.persistSavedLoginPreference(remember, loginId);
            } catch (e) { }
            // markMfaVerified will still be called in the non-DMTT path below before navigation
          } else {
            // For DMTT do not finalize login into OTB (prevents brief OTB dashboard render)
            try {
              this.persistSavedLoginPreference(remember, loginId);
            } catch (e) { }
          }
          this.pendingPassword = null;
          this.pendingLoginResponse = null;
          this.authService.markMfaVerified();
          try {
            const svc = (
              this.currentService ||
              (typeof sessionStorage !== "undefined"
                ? sessionStorage.getItem("signup_service")
                : null) ||
              ""
            )
              .toString()
              .toUpperCase();
            if (svc === "DMTT") {
              console.log("In dmtt");
              const userObj = dataResponse.user || {};
              const userProfile: any =
                this.tokenService?.getUserProfile?.() || {};
              const profileFromOtp = {
                binNumber: userObj?.binNumber || userObj?.userId || "",
                email:
                  userObj?.email ||
                  userProfile?.email ||
                  this.formValues["mfaEmail"] ||
                  null,
                persona:
                  userObj?.persona ||
                  userObj?.profiles ||
                  userObj?.role ||
                  userProfile?.role ||
                  userProfile?.profile ||
                  [],
                tinNumber:
                  userObj?.tinNumber ||
                  userObj?.TIN ||
                  userObj?.tin ||
                  userProfile?.tin ||
                  null,
                taxType: userObj?.taxType || userObj?.tax_type || null,
                firstName: userObj?.firstName || userObj?.firstname || null,
                lastName: userObj?.lastName || userObj?.lastname || null,
                mobileNumber:
                  userObj?.mobileNumber ||
                  userObj?.mobile ||
                  userObj?.phone ||
                  userProfile?.phone ||
                  this.formValues["mfaMobile"] ||
                  null,
                isFirstTimeLogin: userObj?.isFirstTimeLogin || null,
                applicationId:
                  userObj?.applicationId ||
                  userObj?.applicationid ||
                  userObj?.userId ||
                  "",
                registeredName:
                  userObj?.registeredName || userObj?.registered_name || "",
              };

              const payload = {
                token: dataResponse.token || "",
                profile: profileFromOtp,
              };

              const encryptedPayload = this.encryptionService.encrypt(payload);
              const target = environment.dmttDashboardUrl;
              this.alertService.show("Login Successful", {
                type: "success",
                duration: 3000,
              });
              window.location.href = `${target}?dmtt_token=${encodeURIComponent(encryptedPayload)}`;
            } else if (svc === "VAT") {
              const userObj = dataResponse.user || {};
              const userProfile: any =
                this.tokenService?.getUserProfile?.() || {};
              const profileFromOtp = {
                binNumber: userObj?.binNumber || userObj?.userId || "",
                email:
                  userObj?.email ||
                  userProfile?.email ||
                  this.formValues["mfaEmail"] ||
                  null,
                persona:
                  userObj?.persona ||
                  userObj?.profiles ||
                  userObj?.role ||
                  userProfile?.role ||
                  userProfile?.profile ||
                  [],
                tinNumber:
                  userObj?.tinNumber ||
                  userObj?.TIN ||
                  userObj?.tin ||
                  userProfile?.tin ||
                  null,
                taxType: userObj?.taxType || userObj?.tax_type || null,
                firstName: userObj?.firstName || userObj?.firstname || null,
                lastName: userObj?.lastName || userObj?.lastname || null,
                mobileNumber:
                  userObj?.mobileNumber ||
                  userObj?.mobile ||
                  userObj?.phone ||
                  userProfile?.phone ||
                  this.formValues["mfaMobile"] ||
                  null,
                isFirstTimeLogin: userObj?.isFirstTimeLogin || null,
                applicationId:
                  userObj?.applicationId ||
                  userObj?.applicationid ||
                  userObj?.userId ||
                  "",
                registeredName:
                  userObj?.registeredName || userObj?.registered_name || "",
              };

              const payload = {
                token: dataResponse.token || "",
                profile: profileFromOtp,
              };

              const encryptedPayload = this.encryptionService.encrypt(payload);
              const target = environment.vatDashboardUrl;
              this.alertService.show("Login Successful", {
                type: "success",
                duration: 3000,
              });
              window.location.href = `${target}?token=${encodeURIComponent(encryptedPayload)}`;
            } else if (svc === "BL") {
              const userObj = dataResponse.user || {};
              const userProfile: any =
                this.tokenService?.getUserProfile?.() || {};
              const profileFromOtp = {
                binNumber: userObj?.binNumber || userObj?.userId || "",
                email:
                  userObj?.email ||
                  userProfile?.email ||
                  this.formValues["mfaEmail"] ||
                  null,
                persona:
                  userObj?.persona ||
                  userObj?.profiles ||
                  userObj?.role ||
                  userProfile?.role ||
                  userProfile?.profile ||
                  [],
                tinNumber:
                  userObj?.tinNumber ||
                  userObj?.TIN ||
                  userObj?.tin ||
                  userProfile?.tin ||
                  null,
                taxType: userObj?.taxType || userObj?.tax_type || null,
                firstName: userObj?.firstName || userObj?.firstname || null,
                lastName: userObj?.lastName || userObj?.lastname || null,
                mobileNumber:
                  userObj?.mobileNumber ||
                  userObj?.mobile ||
                  userObj?.phone ||
                  userProfile?.phone ||
                  this.formValues["mfaMobile"] ||
                  null,
                isFirstTimeLogin: userObj?.isFirstTimeLogin || null,
                applicationId:
                  userObj?.applicationId ||
                  userObj?.applicationid ||
                  userObj?.userId ||
                  "",
                registeredName:
                  userObj?.registeredName || userObj?.registered_name || "",
              };

              const payload = {
                token: dataResponse.token || "",
                profile: profileFromOtp,
              };

              const encryptedPayload = this.encryptionService.encrypt(payload);
              const target = environment.blDashboardUrl;
              this.alertService.show("Login Successful", {
                type: "success",
                duration: 3000,
              });
              window.location.href = `${target}?token=${encodeURIComponent(encryptedPayload)}`;
            }
            else {
              this.authService.markMfaVerified();
              this.router.navigate(["/dashboard"]);
            }
          } catch (e) {
            this.authService.markMfaVerified();
            this.router.navigate(["/dashboard"]);
          }
          // this.router.navigate(['/dashboard']);
          return;
        }

        this.pendingLoginResponse = response;
        this.pendingPassword = this.formValues["password"] || null;
        this.setActiveForm("mfaLoginForm");
      },
      error: (error) => {
        this.showError = true;
        const backend = error?.error || {};
        const errMsg =
          backend.message || "Login failed. Please check your credentials.";

        const attempts =
          Number(backend.login_attempts || backend.loginAttempts || 0) || 0;
        const locked = backend.locked === true || attempts >= 3;

        if (locked) {
          this.activationSent = true;
          this.accountLocked = true;
          // Store registered email from backend if provided so user can resend
          if (backend.registeredEmail)
            this.formValues["registeredEmail"] = backend.registeredEmail;
          const masked = backend.registeredEmail
            ? maskEmail(backend.registeredEmail)
            : this.formValues["registeredEmail"]
              ? maskEmail(this.formValues["registeredEmail"])
              : null;
          this.activationMessage = `Your account has been locked due to multiple unsuccessful login attempts. You can follow the instructions sent in mail to reset the password.`;
          try {
            if (masked)
              this.alertService.show(
                "Instructions to reset your password were sent to " +
                masked +
                ". Please follow them to regain access.",
                { type: "success", duration: 8000 },
              );
            else
              this.alertService.show(
                "Your account is locked. Follow the instructions sent to your registered email to reset your password.",
                { type: "error", duration: 8000 },
              );
          } catch (e) { }

          try {
            this.startActivationTimer(60);
          } catch (e) {
            /* ignore */
          }
          this.logger.warn(
            `Account locked for user ${this.formValues["userId"]} after ${attempts} failed attempts`,
          );
        } else {
          this.accountLocked = false;
          this.alertService.show(errMsg, { type: "error", duration: 5000 });
        }

        this.failedAttempts = Math.max(this.failedAttempts, attempts);
        this.isSubmitting = false;
      },
    });
  }

  @HostListener("window:resize", ["$event"])
  onResize(event: any) {
    this.isSmallScreen = event.target.innerWidth < 400;
  }

  @HostListener("document:keydown.enter", ["$event"])
  handleGlobalEnter(event: Event) {
    const keyboardEvent = event as KeyboardEvent;
    try {
      if (!keyboardEvent) return;
      if ((keyboardEvent as any).defaultPrevented) return;
      const active =
        typeof document !== "undefined"
          ? (document.activeElement as HTMLElement | null)
          : null;
      if (!active) return;
      try {
        if (
          active.getAttribute &&
          active.getAttribute("data-enter-handled") === "true"
        )
          return;
      } catch (e) {
        /* ignore */
      }
      const dataKey = active.getAttribute && active.getAttribute("data-key");
      if (dataKey) {
        const fields = this.signUpData?.forms?.[this.activeForm]?.fields || [];
        const idx = fields.findIndex((f: any) => f.key === dataKey);
        if (idx >= 0) {
          this.onFieldEnter(event, fields[idx], idx);
          return;
        }
      }

      if (active.tagName === "BUTTON") {
        (active as HTMLButtonElement).click();
        return;
      }

      const form = active.closest && active.closest("form");
      if (this.clickPrimaryButton(form)) return;
      this.clickPrimaryButton(null);
    } catch (e) {
      console.log("WARN: handleGlobalEnter error", e);
    }
  }

  onFieldEnter(evt: any, field: any, index: number) {
    try {
      if (evt && typeof evt.preventDefault === "function") {
        evt.preventDefault();
        evt.stopPropagation();
      }
      const key = field?.key || "";

      if (key === "mfaEmail" || key === "mfaMobile") {
        this.sendMfaOtp();
        return;
      }

      if (key === "registeredEmail") {
        this.sendOtp();
        const sendBtnDef = this.getButtonDef("sendOtp");
        if (sendBtnDef) {
          this.onButtonClick(sendBtnDef);
          return;
        }
        const targetForm =
          evt && evt.target && (evt.target as HTMLElement).closest
            ? (evt.target as HTMLElement).closest("form")
            : null;
        if (this.clickPrimaryButton(targetForm, ["send-otp"])) return;
        this.clickPrimaryButton(null, ["send-otp"]);
        return;
      }

      const fields = this.signUpData?.forms?.[this.activeForm]?.fields || [];
      let lastInteractiveIndex = -1;
      for (let i = 0; i < fields.length; i++) {
        if (fields[i].type !== "checkbox") lastInteractiveIndex = i;
      }
      const isFinal =
        typeof index === "number" ? index === lastInteractiveIndex : false;

      if (isFinal) {
        if (field && field.required) {
          const v = this.formValues[field.key];
          if (!v || (typeof v === "string" && v.trim() === "")) {
            this.showError = true;
            this.focusField(field.key);
            return;
          }
        }

        const submitBtnDef = this.getButtonDef("submitForm");
        if (submitBtnDef) {
          this.onButtonClick(submitBtnDef);
          return;
        }

        this.onSubmit();
        return;
      }

      this.advanceStep();
    } catch (e) {
      console.log("WARN: onFieldEnter error", e);
    }
  }

  handleEnterOnField(evt: any) {
    try {
      if (evt && typeof evt.preventDefault === "function") {
        evt.preventDefault();
        evt.stopPropagation();
      }
      const target = evt && (evt.target as HTMLElement | null);
      try {
        if (target && target.setAttribute)
          target.setAttribute("data-enter-handled", "true");
      } catch (e) { }
      this.advanceStep();
      setTimeout(() => {
        try {
          if (target && target.removeAttribute)
            target.removeAttribute("data-enter-handled");
        } catch (e) { }
      }, 50);
    } catch (e) {
      console.log("WARN: handleEnterOnField error", e);
    }
  }

  onFormEnter(evt: any) {
    try {
      if (evt && typeof evt.preventDefault === "function") {
        evt.preventDefault();
        evt.stopPropagation();
      }
      const active =
        typeof document !== "undefined"
          ? (document.activeElement as HTMLElement | null)
          : null;
      if (active) {
        const dataKey = active.getAttribute && active.getAttribute("data-key");
        if (dataKey) {
          const fields =
            this.signUpData?.forms?.[this.activeForm]?.fields || [];
          const idx = fields.findIndex((f: any) => f.key === dataKey);
          if (idx >= 0) {
            this.onFieldEnter(evt, fields[idx], idx);
            return;
          }
        }
      }

      if (active && active.closest) {
        if (this.clickPrimaryButton(active.closest("form"))) return;
      }
      this.clickPrimaryButton(null);
    } catch (e) {
      console.log("WARN: onFormEnter error", e);
    }
  }

  private clickPrimaryButton(
    scope?: Element | Document | null,
    preferredActions?: string[],
  ): boolean {
    if (typeof document === "undefined") return false;
    try {
      const root: ParentNode = (scope as ParentNode) || document;
      const actions =
        preferredActions && preferredActions.length
          ? preferredActions
          : ["send-otp", "submitForm", "verify-otp", "verifyMfaOtp"];
      for (const action of actions) {
        if (action === "verifyMfaOtp") {
          const otp = this.formValues["mfaOtp"] || "";
          if (
            !this.mfaOtpSent ||
            !otp ||
            (typeof otp === "string" && otp.length < this.inlineOtpLength)
          ) {
            this.focusOtpInput();
            continue;
          }
        }
        const btn =
          root.querySelector &&
          (root.querySelector(
            `button[data-action="${action}"]:not([disabled])`,
          ) as HTMLButtonElement | null);
        if (btn) {
          btn.click();
          return true;
        }
      }
      const submitBtn =
        root.querySelector &&
        (root.querySelector(
          "button.submit-btn:not([disabled])",
        ) as HTMLButtonElement | null);
      if (submitBtn) {
        submitBtn.click();
        return true;
      }
    } catch (e) {
      console.log("WARN: clickPrimaryButton error", e);
    }
    return false;
  }

  private focusField(fieldKey: string, delay: number = 0) {
    if (typeof document === "undefined" || !fieldKey) return;
    setTimeout(() => {
      const el =
        (document.querySelector(
          `[data-key="${fieldKey}"]`,
        ) as HTMLElement | null) ||
        document.getElementById(fieldKey) ||
        (document.querySelector(
          `#signup-field-${this.currentStep}`,
        ) as HTMLElement | null);
      if (el && typeof (el as any).focus === "function") {
        (el as any).focus();
      }
    }, delay);
  }

  private focusOtpInput(targetIndex: number = 0) {
    if (typeof document === "undefined") return;
    setTimeout(() => {
      const inputs = document.querySelectorAll(
        ".otp-input-box input",
      ) as NodeListOf<HTMLInputElement>;
      if (!inputs || !inputs.length) return;
      const idx = Math.max(0, Math.min(targetIndex, inputs.length - 1));
      const el = inputs[idx] || inputs[0];
      if (el && typeof el.focus === "function") el.focus();
    }, 50);
  }

  private focusFirstVisibleField(delay: number = 150) {
    const fields = this.signUpData?.forms?.[this.activeForm]?.fields || [];
    if (!fields.length) {
      this.focusField("userId", delay);
      return;
    }
    const firstVisible = fields.find((f: any, idx: number) =>
      this.showField(f, idx),
    );
    const key = firstVisible?.key || fields[0]?.key || "userId";
    this.focusField(key, delay);
  }

  private scrollToElementIfNeeded(el: HTMLElement | null) {
    try {
      if (!el || typeof window === "undefined") return;
      setTimeout(() => {
        try {
          const rect = el.getBoundingClientRect();
          const viewportHeight =
            window.innerHeight || document.documentElement.clientHeight;
          const buffer = 120;
          if (rect.bottom > viewportHeight - buffer) {
            const scrollBy = rect.bottom - (viewportHeight - buffer);
            window.scrollBy({
              top: scrollBy + 12,
              left: 0,
              behavior: "smooth",
            });
          } else if (rect.top < 0) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        } catch (e) {
          /* ignore scroll errors */
        }
      }, 80);
    } catch (e) {
      /* ignore */
    }
  }

  preventPaste(evt: ClipboardEvent) {
    try {
      const target = evt.target as HTMLElement | null;
      if (target && target.closest && target.closest(".otp-input-box")) return; // OTP paste already blocked globally, but keep guard
      evt.preventDefault();
      evt.stopPropagation();
      this.showBlockedClipboardMessage();
    } catch (e) {
      /* ignore */
    }
  }

  preventCopy(evt: ClipboardEvent) {
    try {
      evt.preventDefault();
      evt.stopPropagation();
    } catch (e) { }
  }

  preventCut(evt: ClipboardEvent) {
    try {
      evt.preventDefault();
      evt.stopPropagation();
    } catch (e) { }
  }

  preventContext(evt: MouseEvent) {
    try {
      evt.preventDefault();
      evt.stopPropagation();
    } catch (e) { }
  }

  preventDrop(evt: DragEvent) {
    try {
      evt.preventDefault();
      evt.stopPropagation();
    } catch (e) { }
  }

  private showBlockedClipboardMessage() {
    try {
      if (this.blockedMessageShown) return;
      this.blockedMessageShown = true;
      if (this.alertService && typeof this.alertService.show === "function") {
        this.alertService.show(
          "This field doesn't allow copy or paste for security reasons.",
          { type: "info", duration: 4000 },
        );
      } else {
        try {
          alert("This field doesn't allow copy or paste for security reasons.");
        } catch (e) { }
      }
      if (this.blockedMessageTimeout) clearTimeout(this.blockedMessageTimeout);
      this.blockedMessageTimeout = setTimeout(() => {
        this.blockedMessageShown = false;
        this.blockedMessageTimeout = null;
      }, 3000);
    } catch (e) {
      /* ignore */
    }
  }

  private buildCaptchaCode(): string {
    const allowedChars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    const length = 6;
    let captcha = "";
    const randomValues = new Uint32Array(length);
    crypto.getRandomValues(randomValues);
    for (let i = 0; i < length; i++) {
      captcha += allowedChars[randomValues[i] % allowedChars.length];
    }
    return captcha;
  }

  generateCaptcha(): void {
    this.captchaCode = this.buildCaptchaCode();
    setTimeout(() => this.drawCaptcha(), 50);
  }

  drawCaptcha(): void {
    if (typeof window !== "undefined" && typeof document !== "undefined") {
      const canvas = document.getElementById(
        this.canvasId,
      ) as HTMLCanvasElement | null;
      const ctx = canvas?.getContext("2d");
      if (!ctx || !canvas) return;

      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      const fontSize = Math.floor(canvasWidth / 4.1);

      ctx.clearRect(0, 0, canvasWidth, canvasHeight);
      ctx.fillStyle = "#E8E9F0";
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);

      ctx.font = `${fontSize}px Arial`;
      ctx.fillStyle = "#1A256B";
      ctx.textBaseline = "middle";

      const textWidth = ctx.measureText(this.captchaCode).width;
      const x = (canvasWidth - textWidth) / 2;
      const y = canvasHeight / 2;

      ctx.fillText(this.captchaCode, x, y);

      ctx.strokeStyle = "#1A256B";
      for (let i = 0; i < 10; i++) {
        ctx.beginPath();
        ctx.moveTo(Math.random() * canvasWidth, Math.random() * canvasHeight);
        ctx.lineTo(Math.random() * canvasWidth, Math.random() * canvasHeight);
        ctx.stroke();
      }
    }
  }

  verifyCaptcha(): boolean {
    try {
      this.captchaError = null;
      const raw =
        this.captchaControl && this.captchaControl.value != null
          ? this.captchaControl.value
          : "";
      const input = String(raw);
      if (input !== (this.captchaCode || "")) {
        try {
          this.captchaControl.setErrors?.({ captchaMismatch: true });
        } catch (e) {
          /* ignore */
        }
        return false;
      } else {
        try {
          this.captchaControl.setErrors?.(null);
        } catch (e) {
          /* ignore */
        }
        return true;
      }
    } catch (err) {
      try {
        this.captchaControl.setErrors?.({ captchaMismatch: true });
      } catch (e) {
        /* ignore */
      }
      return false;
    }
  }

  refreshCaptcha(): void {
    this.generateCaptcha();
    try {
      this.captchaControl.setValue("");
    } catch (e) {
      /* ignore */
    }
  }

  ngOnDestroy(): void {
    try {
      this.loaderService.hide();
      if (this.arrowKeydownListener && typeof document !== "undefined") {
        document.removeEventListener(
          "keydown",
          this.arrowKeydownListener,
          true,
        );
        this.arrowKeydownListener = null;
      }
      try {
        if (typeof document !== "undefined") {
          document.body.classList.remove("signup-page-bg");
        }
      } catch (e) {
        /* ignore */
      }
    } catch (err) {
      console.log("WARN: Error tearing down arrow key listener", err);
    }
  }
}
